// Transaction Object Model types matching the JSON schema

import type { DisplayFormat } from './displayFormat'
import type { SendActionBinding, SendWhenRule } from '../messaging/types'
import type { UdaColumnMarker, UdaLayer, UdaPictures } from '../uda/types'
import type { ExportFormat, ExportPlace, ExportTemplate } from './export/types'
export { buildSendActionRequest } from '../messaging/types'
export type { SendActionBinding, SendActionRequest } from '../messaging/types'

export interface EditMaskValue {
  data: string
  display: string
}

// Widget masks. The first group are choice/boolean widgets that carry
// `values`; the second group ('textarea' | 'url' | 'image' | 'document')
// are presentation widgets over a plain text/varchar column:
//   - textarea  : multi-line auto-growing text box (capped height)
//   - url       : single-line URL with an "open link" affordance
//   - image     : file upload that stores the document-store URL of an
//                 image and renders a thumbnail preview
//   - document  : file upload that stores the document-store URL of any
//                 document (pdf/office/csv/…) and renders a file chip
export interface EditMask {
  mask: 'ddlb' | 'textbox' | 'checkbox' | 'radio' | 'bool_check'
    | 'textarea' | 'url' | 'image' | 'document' | ''
  values: EditMaskValue[]
  // bool_check only: draw it as an on/off switch (Vision's Yes / No toggle)
  // instead of a check box. Values and storage are unchanged.
  // radio only: 'chips' draws each option as a pill (the radio input stays,
  // visually hidden, so keyboard and screen-reader behaviour is unchanged).
  style?: 'toggle' | 'chips'
  // textarea only: its height in lines (a Vision multi-line field's).
  rows?: number
}

// A fill mapping is one of two kinds (mutually exclusive):
//   - lookup fill: copy `table_col` from the matched source-table row.
//   - expression fill: set `form_col` to the result of `expr`, an
//     arithmetic expression over current-form columns (bare names or
//     {form.X}), parent/header-form columns ({header.X}) and the helper
//     functions round/abs/min/max/iif. Evaluated server-side after the
//     lookup fills, so it can reference freshly-filled columns.
export interface ItemChangeFillMapping {
  form_col: string
  table_col?: string
  expr?: string
}

// Conditional (un)protect entry: after the rule's fills run, `when` is
// evaluated server-side (same expression language as expression fills) and
// `form_col` is protected (truthy → 1) or unprotected (falsy → 0) via the
// response's `protect` map — the declarative form of the BL-mode
// {"value": v, "protect": "1"|"0"} cell contract.
export interface ItemChangeProtectMapping {
  form_col: string
  when: string
}

export interface ItemChangeRule {
  mode: 'rule'
  source_table: string
  match_mode?: 'simple' | 'advanced'
  match_table_col?: string
  where_sql?: string
  fill: ItemChangeFillMapping[]
  protect?: ItemChangeProtectMapping[]
  on_no_match?: 'clear' | 'leave'
  on_multi_match?: 'first' | 'warn' | 'open_lookup'
}

export interface ItemChangeCode {
  mode: 'code'
  code: string
}

// item_change driven by a business-logic function (Python / PL-pgSQL / Java).
// The function "sets values based on the definition by matching column name",
// and may also (un)protect columns, target multiple forms, and add rows — see
// ItemChangeResult. Reuses the validation BusinessLogicValidation shape
// (method + description + language).
export interface ItemChangeBusinessLogic {
  mode: 'business_logic'
  business_logic: BusinessLogicValidation
}

// item_change driven by an external "Call API" endpoint. The response body is
// the same return contract as business_logic (flat / forms / Detail XML).
export interface ItemChangeRestApi {
  mode: 'rest_api'
  rest_api: RestApiValidation
}

// The active item_change modes are Rule (lookup + expression fills),
// Business Logic and Call API. ItemChangeCode is legacy-readable only (an old
// `business_logic('fn')` string), normalised to business_logic by
// normalizeItemChange.
export type ItemChange =
  | ItemChangeRule
  | ItemChangeBusinessLogic
  | ItemChangeRestApi
  | ItemChangeCode

// ── row_complete (form-level "row finished" event) ───────────────────────
//
// An ItemChange plus two optional row_complete-only settings:
//  - rebuild: the child form the event rebuilds. Rows the function returns for
//    it without an explicit flag are treated as a replace (`mode` 'append'
//    keeps the add-rows behaviour). A form the function does not mention is
//    left alone — that is how it says "nothing changed".
//  - when_changed: a client-side shortcut — skip the call entirely when none
//    of these columns differ from the row's previous values (an existing row
//    only; a new row always fires). Optional: the function itself decides via
//    `_old` either way.
export interface RowCompleteSettings {
  rebuild?: { form_no: string; mode?: 'replace' | 'append' }
  when_changed?: string[]
}
export type RowCompleteEvent = ItemChange & RowCompleteSettings

// ── item_change / row-event return contract ──────────────────────────────
//
// What a business_logic / Call-API item_change (or row_add / row_edit) returns,
// normalised by the engine. `updates` / `protect` are the CURRENT form's
// changes (back-compat); `forms` is the full per-form map keyed by form_no
// (e.g. "1" = header). A form change is a single-row `set` (+ `protect`), and/or
// `rows` for a child form. `rows` are ADDED unless `replace` is set: then the
// form's existing rows are deleted first (`replace` with no rows = clear —
// delete all, add none). `unchanged` says the function looked and decided to
// leave the form's rows exactly as they are (the row_complete contract).
export interface ItemChangeFormChange {
  set?: Record<string, unknown>
  protect?: Record<string, 0 | 1>
  rows?: Array<Record<string, unknown>>
  replace?: boolean
  unchanged?: boolean
}

export interface ItemChangeResult {
  ok?: boolean
  action?: string
  updates?: Record<string, unknown>
  protect?: Record<string, 0 | 1>
  forms?: Record<string, ItemChangeFormChange>
  error?: string
  warning?: string
  open_lookup?: boolean
}

// ── Validations (structured) ─────────────────────────────────────────────
//
// A validation is EITHER a legacy function-call string (e.g.
// `must_exist_in('CUSTOMERS')`, `business_logic('fn','desc')`) — still read
// and executed by the engine for back-compat — OR a structured
// `ValidationRule` object. New validations authored in the designer / by the
// AI are objects: they carry a friendly name, an optional unique error code,
// and an error message that may interpolate {form.X} / {header.X} columns.

export type ValidationType = 'builtin' | 'expression' | 'business_logic' | 'rest_api'

// Where a value comes from: a literal constant, a current-form column, the
// header/parent-form column of a master-detail transaction, or (range bounds
// only) an expression.
export type ValueSource = 'const' | 'form' | 'header' | 'expr'

export interface ValueRef {
  source: ValueSource
  // The constant text (source='const'), the column name (source='form'|'header'),
  // or — for range bounds — an expression (source='expr') that may use {form.X} /
  // {header.X} and date helpers: today(), now(), date(y,m,d), days_ago(n),
  // days_from_now(n), years_ago(n), years_from_now(n), plus round/abs/min/max/iif.
  // e.g. years_ago(20) or today() - (20 * 365). Date bounds compare by calendar date.
  value: string
}

export type BuiltinKind =
  | 'must_exist_in_master'
  | 'must_be_unique'
  | 'month_open'
  | 'child_form_has_entry'
  | 'value_in_range'
  | 'sql_range'

// One extra column-value match applied to the looked-up master row. The match
// passes when `master.column <op> <value>` — or, when `allow_null` is set, also
// when `master.column IS NULL`. `value` resolves per `source` (const/form/header).
export interface MasterCondition {
  column: string
  op?: '=' | '!=' | '>' | '<' | '>=' | '<=' | 'in'  // default '='
  source: ValueSource
  value: string
  allow_null?: boolean
}

export interface BuiltinValidation {
  kind: BuiltinKind

  // kind='must_exist_in_master': the field value must exist in `table`
  // (matched on `match_column`, defaulting to the column's lookup data column
  // / db_name), optionally satisfying extra `conditions` on the master row.
  //
  // kind='must_be_unique': no OTHER row may already hold this value in
  // `match_column` (default: this field's own column) of `table` (default:
  // the form's main table); optional `conditions` scope the duplicate search
  // (e.g. unique within a company). On edit the engine excludes the record
  // being edited by primary key, so re-saving a record never flags itself.
  table?: string
  match_column?: string
  conditions?: MasterCondition[]

  // kind='month_open': a row in `month_table` must contain the checked date
  // between `from_date_column` and `to_date_column` AND have
  // `status_column` = `status_open_value` (default 'Y'). The checked date is
  // `date_value` when set, else this field's value.
  month_table?: string
  from_date_column?: string
  to_date_column?: string
  date_value?: ValueRef
  status_column?: string
  status_open_value?: string

  // kind='child_form_has_entry' (form/header-level): the child form named by
  // `child_form` (its form_no) must have at least `min_rows` (default 1) rows.
  child_form?: string
  min_rows?: number

  // kind='value_in_range': the field value must be >= `min` and <= `max`
  // (each a constant, a column, or an expression — see ValueRef). Date fields
  // compare by calendar date, so an age check is e.g. max = years_ago(20).
  min?: ValueRef
  max?: ValueRef

  // kind='sql_range': `sql` (a single-value SELECT that may reference
  // {form.X}/{header.X}) must return a value within [range_min, range_max].
  sql?: string
  range_min?: ValueRef
  range_max?: ValueRef
}

export interface RestApiParam {
  name: string
  source: ValueSource
  value: string
}

export interface RestApiValidation {
  url: string
  // IOFlow-routed API (from the project's Integration design). When set,
  // the engine invokes the IOFlow `api.<name>` event (sync) instead of the
  // raw URL — url/method/auth fields are kept only as documentation /
  // fallback for hosts with no IOFlow connection configured.
  ioflow_api?: string
  method?: 'GET' | 'POST'             // default POST
  // Header NAME for the auth token (default 'Authorization').
  auth_header?: string
  // Token value — a literal, or a {var.NAME} project-variable reference.
  auth_token?: string
  // Column / constant values posted to the endpoint.
  params?: RestApiParam[]
  // What to do when the endpoint is unreachable / errors: 'allow' (fail-soft,
  // log & pass — default) or 'block' (fail the save).
  on_error?: 'allow' | 'block'
}

export interface BusinessLogicValidation {
  method: string
  // Short one-line label, shown in lists / summaries.
  description?: string
  // Functional specification — the detailed natural-language requirement that
  // drives LLM/agent code generation and is written into the generated code
  // file's header. Multi-line. The transaction model JSON is the source of
  // truth; the header is regenerated from it.
  spec?: string
  language?: 'plpgsql' | 'plsql' | 'python' | 'java'
}

// Build the back-compat call-string the engine / legacy readers understand
// from a structured business-logic object. The functional specification is
// authoring-only metadata (used for codegen + the file header) and is never
// encoded in the call-string. Positional: ('method','description'[,'language']).
export function businessLogicCallString(bl: BusinessLogicValidation | null | undefined): string {
  const method = (bl?.method || '').trim()
  if (!method) return ''
  const desc = bl?.description || ''
  const lang = (bl?.language || '').trim()
  const parts = [method, desc]
  if (lang) parts.push(lang)
  return `business_logic(${parts.map((a) => `'${a}'`).join(',')})`
}

// Parse a legacy `business_logic('m','d'[,'l'])` call-string into the structured
// shape. Returns null when the string isn't a business_logic(...) call.
export function parseBusinessLogicCallString(str: string): BusinessLogicValidation | null {
  const raw = (str || '').trim()
  const m = raw.match(/^business_logic\((.*)\)$/s)
  if (!m) return null
  const args = parseCallArgs(m[1] || '')
  return {
    method: args[0] || '',
    description: args[1] || '',
    language: (args[2] as BusinessLogicValidation['language']) || undefined,
  }
}

// How a failing rule is surfaced:
//   'E' error   — blocks the save (default).
//   'W' warning — blocks until the user picks "ignore warnings and proceed";
//                 the retry passes the rule's code (error_code, else name) in
//                 ignore_warnings and the engine skips it.
//   'P' prompt  — information only: the message is shown to the user but the
//                 save proceeds, no confirmation needed.
export type ValidationSeverity = 'E' | 'W' | 'P'

export interface ValidationRule {
  type: ValidationType
  // User-friendly name of the validation.
  name: string
  // Optional unique error code, prefixed onto the message at runtime. For a
  // severity-'W' rule it is ALSO the warning identity used by "ignore and
  // proceed" (falls back to the rule name).
  error_code?: string
  // Error description; may interpolate {form.X} / {header.X} column values.
  message: string
  // 'E' (default) blocks; 'W' user may acknowledge and proceed; 'P' info only.
  severity?: ValidationSeverity
  // When true, the designer has temporarily switched this rule OFF. The engine
  // skips it (and logs that it was disabled) instead of evaluating it.
  disabled?: boolean
  // The operations this rule runs for — 'add' / 'edit' (a save) or an action
  // button's name ('confirm'). Unset = every save. A rule naming an action is
  // skipped on save and checked when that action runs (a legacy GENMST rule
  // whose ACTION was Confirm imports this way).
  run_on?: string[]
  // Skip the rule while its own field is blank (legacy GENMST BLANK_OPT='Y').
  skip_if_blank?: boolean
  // General-Process only: the RESULT-grid column this rule applies to — the
  // column whose value a built-in check reads, and the cell an error highlights.
  // The transaction designer attaches a rule to a field instead, so it is unset
  // there and the engine ignores it.
  column?: string

  builtin?: BuiltinValidation
  // type='expression': passes when the expression is truthy (item-change
  // expression syntax — bare names / {form.X} / {header.X} + round/abs/min/max/iif).
  expression?: string
  business_logic?: BusinessLogicValidation
  rest_api?: RestApiValidation
}

// A validations[] entry: a legacy string or a structured rule.
export type Validation = string | ValidationRule

export const VALIDATION_TYPE_LABELS: Record<ValidationType, string> = {
  builtin: 'Built-in',
  expression: 'Expression',
  business_logic: 'Business Logic',
  rest_api: 'Call API',
}

export const VALIDATION_SEVERITY_LABELS: Record<ValidationSeverity, string> = {
  E: 'Error — blocks the save',
  W: 'Warning — user can ignore and proceed',
  P: 'Prompt — information only, never blocks',
}

export const BUILTIN_KIND_LABELS: Record<BuiltinKind, string> = {
  must_exist_in_master: 'Must exist in master',
  must_be_unique: 'Must be unique',
  month_open: 'Month must be open',
  child_form_has_entry: 'Child form must have an entry',
  value_in_range: 'Value must fall in a range',
  sql_range: 'SQL query result in range',
}

export function isValidationRule(v: Validation | null | undefined): v is ValidationRule {
  return !!v && typeof v === 'object' && typeof (v as ValidationRule).type === 'string'
}

// Split `a,'b,c'` honouring single-quoted args (mirrors the engine + legacy
// editor parser). Used to upgrade an old function-call string into the editor.
function parseCallArgs(argsStr: string): string[] {
  const args: string[] = []
  let current = ''
  let inQuote = false
  for (const ch of argsStr) {
    if (ch === "'" && !inQuote) { inQuote = true; continue }
    if (ch === "'" && inQuote) { inQuote = false; continue }
    if (ch === ',' && !inQuote) { args.push(current.trim()); current = ''; continue }
    current += ch
  }
  if (current.trim() || args.length) args.push(current.trim())
  return args
}

// Convert a legacy validation string into a structured rule the editor can
// present. Returns null when the string isn't a recognised call — the caller
// then keeps it as a raw legacy string (still executed by the engine).
export function legacyStringToRule(str: string): ValidationRule | null {
  const raw = (str || '').trim()
  const m = raw.match(/^(\w+)\((.*)\)$/s)
  if (!m) return null
  const fn = m[1].toLowerCase()
  const args = parseCallArgs(m[2] || '')
  switch (fn) {
    case 'must_exist_in':
      return {
        type: 'builtin', name: `Must exist in ${args[0] || ''}`.trim(), message: '',
        builtin: { kind: 'must_exist_in_master', table: args[0] || '', match_column: args[1] || '' },
      }
    case 'business_logic':
      return {
        type: 'business_logic', name: args[0] || 'Business logic', message: '',
        business_logic: {
          method: args[0] || '', description: args[1] || '',
          language: (args[2] as BusinessLogicValidation['language']) || undefined,
        },
      }
    case 'expression':
    case 'javascript':
      return { type: 'expression', name: 'Expression', message: '', expression: args[0] || '' }
    case 'rest_service':
    case 'rest_api':
      return {
        type: 'rest_api', name: args[0] || 'Call API', message: args[1] || '',
        rest_api: { url: args[0] || '', on_error: 'allow' },
      }
    default:
      return null
  }
}

// One-line summary for the property panel row.
export function validationSummary(v: Validation): string {
  if (!isValidationRule(v)) return (v || '').toString()
  const label = VALIDATION_TYPE_LABELS[v.type] || v.type
  let detail = ''
  if (v.type === 'builtin' && v.builtin) {
    detail = BUILTIN_KIND_LABELS[v.builtin.kind] || v.builtin.kind
  } else if (v.type === 'business_logic' && v.business_logic) {
    detail = v.business_logic.method
  } else if (v.type === 'rest_api' && v.rest_api) {
    detail = v.rest_api.url
  } else if (v.type === 'expression' && v.expression) {
    detail = v.expression
  }
  const head = v.name || label
  const code = v.error_code ? `[${v.error_code}] ` : ''
  const dis = v.disabled ? '(disabled) ' : ''
  const sev = v.severity === 'W' ? '⚠ warning · '
    : v.severity === 'P' ? 'ℹ prompt · ' : ''
  return detail && detail !== head
    ? `${dis}${sev}${code}${head} · ${label}: ${detail}`
    : `${dis}${sev}${code}${head} · ${label}`
}

// ── Action buttons ──────────────────────────────────────────────────────
// A form / transaction action button. Historically an action was a bare
// string `"name:logic"` (logic = a business-logic / Call-API call-string).
// It is now EITHER that legacy string OR a structured ``ActionDef`` carrying
// a configurable result handler plus optional PRE-ACTION validations (the
// SAME ``Validation[]`` shape as a form's ``validations`` — they run first;
// on any failure the action is blocked and the error shown; with none
// defined the action's logic runs directly).

// What the action does once its pre-action validations pass:
//  - 'system'      built-in CRUD (add / edit / delete / save)
//  - 'html_modal'  business logic returns HTML, shown in a modal window
//  - 'message'     business logic runs; its returned message is shown (toast)
//  - 'item_change' business logic return is applied to the form exactly like
//                  an item_change (set by column name, protect, multi-form, rows)
//  - 'select_grid' business logic returns rows shown in a modal grid; the rows
//                  the user selects are applied like item_change (multi-row),
//                  unselected rows ignored
//  - 'report'      opens the bound Jasper report (obj_type "R") in a viewer
//                  modal, with report parameters resolved from the clicked
//                  row via ``report.param_map`` (no business logic runs)
//  - 'assistant'   calls the in-app AI Assistant with values from the screen
//                  (no business logic runs): either runs a DEFINED INTENT
//                  deterministically (mode 'intent' — no LLM) or opens the
//                  Assistant seeded with a rendered prompt (mode 'prompt').
//                  Values bind via {form.COL} / {header.COL} tokens.
//  - 'payment'     opens the APPLICATION'S OWN checkout for this record, so
//                  the customer (or an operator on their behalf) can pay for
//                  it. No business logic runs; the button declares a
//                  `payment` binding of COLUMN NAMES and the server resolves
//                  the amount from the stored record — see PaymentActionBinding.
//  - 'import'      asks for a data file (CSV / Excel / JSON) and opens the
//                  side-by-side import preview over THIS object — the same
//                  pipeline the Assistant's file import uses, with columns
//                  matched by name and fixable on screen. No business logic
//                  runs; the preview's Save All writes through the engine.
//  - 'open'        opens ANOTHER object with values taken from the clicked
//                  row: a transaction in add / edit / edit-or-add mode (its
//                  columns set one by one, each firing its item_change, then
//                  declared follow-up steps such as a Line Entry command that
//                  pulls the detail lines), a General Process with its
//                  criteria filled, a Dashboard / Visual with its filters
//                  filled, or a Report with its prompts filled. Nothing runs
//                  server-side and nothing is saved — the user reviews the
//                  target and saves it under the target's own rules. See
//                  OpenActionBinding.
//  - 'wizard'      starts a Transaction Wizard (obj_type "U") with its
//                  launch inputs filled from the clicked row via
//                  ``wizard.param_map`` (the 'assistant' param_map grammar).
//                  Nothing runs server-side; the wizard's own grant and steps
//                  decide what happens. See WizardActionBinding.
export type ActionHandlerKind =
  | 'system' | 'html_modal' | 'message' | 'item_change' | 'select_grid'
  | 'report' | 'assistant' | 'payment' | 'import' | 'open' | 'wizard' | 'send'

// Whether a button operates on the whole form / record list (generic — e.g.
// Add, "import rows", "generate report") or on one specific existing record
// (e.g. Edit, Delete, "approve this order"). This drives placement at runtime:
//   - 'form' → rendered in the list toolbar, just before the "Add" button (and
//              still in the open-record action bar).
//   - 'row'  → rendered inside each row's 3-dot (⋮) menu, executed against that
//              row, and still in the open-record action bar (acting on the open
//              record).
// When an ActionDef leaves ``scope`` unset the runtime infers a sensible
// default from the handler / system_op via ``actionScope`` below.
export type ActionScope = 'form' | 'row'

// A value the user gives BEFORE an action runs:
//   - 'scan' → the camera scanner (QR + common barcodes); its entry box also
//              takes an external (keyboard-wedge) scanner or a typed code;
//   - 'text' → a box to type the code.
// The value is posted beside the form values under ``field`` (default
// CAPTURED_VALUE), so the action's business logic reads it like a column.
// Cancelling the prompt runs nothing. It is input, not a rule: the business
// logic still checks it. Applies to non-system handlers only.
//
// ``loop`` (a FORM-scoped item_change action on a LINE form) makes it a scan
// SESSION started from the lines toolbar: scan → the logic answers with the
// line the code belongs to (a one-row list, or ``updates``) or an error →
// the user confirms that line's details (the values it would change shown)
// or cancels → next scan, until they close the scanner. A refused code is
// shown for a few seconds and scanning moves on. ``match`` names the columns
// that find the line among the form's rows (default: its key columns).
// Confirmed values go onto the line like an edit; the record's Save writes
// them. ``pending_while_blank`` names the line column a scan fills: a line
// is still to scan while it is blank — the session closes by itself once
// none is left, and the button is hidden while none is.
export interface ActionCapture {
  mode: 'scan' | 'text'
  // The question shown over the scanner / input box.
  prompt?: string
  field?: string
  loop?: boolean
  match?: string[]
  pending_while_blank?: string
}

// A scan-session action (see ActionCapture.loop) — started from a line
// form's toolbar, never from a record's button bar.
export function isScanSessionAction(def: ActionDef | undefined): boolean {
  return !!actionCaptureOf(def)?.loop && def?.handler === 'item_change'
}

export const DEFAULT_CAPTURE_FIELD = 'CAPTURED_VALUE'

// The action's capture block when it has a usable one (system ops never ask).
export function actionCaptureOf(def: ActionDef | undefined): ActionCapture | null {
  const c = def?.capture
  if (!c || def?.handler === 'system') return null
  return c.mode === 'scan' || c.mode === 'text' ? c : null
}

// The posted values with the captured one added under its field.
export function withCapturedValue(
  values: Record<string, unknown>, capture: ActionCapture, value: string,
): Record<string, unknown> {
  const field = (capture.field || '').trim() || DEFAULT_CAPTURE_FIELD
  return { ...values, [field]: value }
}

export interface ActionDef {
  name: string
  handler: ActionHandlerKind
  // See ActionScope. Optional — designer-settable; otherwise inferred.
  scope?: ActionScope
  // handler='system': which built-in operation the button performs.
  // 'view' merges into the built-in View control (shown when a record is
  // read-only) — its pre-action validations are the conditions that apply when
  // OPENING the record for viewing (the edit action's conditions do not run in
  // view mode).
  // 'export' is the Export system action (docs/plans/transaction-data-export.md):
  // never a button of its own — the screen draws its Export control from it
  // (txn/export exportActionOf) and settings live under `export`.
  system_op?: 'add' | 'edit' | 'delete' | 'save' | 'view' | 'export'
  // Non-system handlers: the action's business logic. Authored as a structured
  // object (the same shape used by item_change / validations — function name,
  // description, language and functional specification) OR, for an external
  // endpoint, a Call-API config.
  business_logic?: BusinessLogicValidation
  rest_api?: RestApiValidation
  // handler='select_grid': the line form the picked rows are added to (by
  // form_no). Absent ⇒ the first child form, as before. Prefer a Line Entry
  // source of kind `business_logic` for new work — it runs the line form's
  // own defaults / item_change and is verified at save.
  target_form_no?: string
  // handler='report': the Report (obj_type "R") this button opens.
  // ``param_map`` maps the report's PROMPT names to values; a "{form.COL}"
  // token resolves against the clicked row (case-insensitive column match),
  // anything else passes through as a literal. ``output_format`` optionally
  // overrides the report's default.
  report?: {
    obj_name: string
    param_map?: Record<string, string>
    output_format?: string
  }
  // handler='assistant': how this button calls the in-app AI Assistant.
  // ``param_map`` / ``prompt`` templates resolve {form.COL} against the
  // CURRENT form's values and {header.COL} against the parent/header form
  // (on a list row both resolve to the row).
  assistant?: AssistantActionBinding
  // handler='payment': what this button charges for. See PaymentActionBinding
  // — every field is a COLUMN NAME, deliberately, so nothing the browser
  // carries can change what is charged.
  payment?: PaymentActionBinding
  // handler='import': how the file import behaves. See ImportActionBinding.
  import?: ImportActionBinding
  // handler='open': which object opens, how, and with which values. See
  // OpenActionBinding.
  open?: OpenActionBinding
  // handler='wizard': which wizard starts and with which inputs. See
  // WizardActionBinding.
  wizard?: WizardActionBinding
  // handler='send': the send window this button opens (preview with the
  // default template, composer, progress). See SendActionBinding — it names
  // templates, channels, attachable R/V/D and lookup masters; the server
  // re-reads it by the action's name and never trusts the browser's copy.
  send?: SendActionBinding
  // WHERE the button is drawn on the list toolbar and the open record's action
  // bar: 'menu' (default) = inside the ⋮ menu, so a screen does not fill up
  // with rarely used buttons; 'bar' = a button on the bar itself. Presentation
  // only — the server ignores it. See actionPlacement().
  placement?: ActionPlacement
  // A keyboard shortcut that runs this button on the landing list (row
  // buttons: on the row last pointed at / selected) and in the open record
  // ("Ctrl+Shift+A", "Alt+R", "F7"). Never a SYSTEM key (F3 Add, F4 Edit,
  // Ctrl+S Save, F10 Save and close …) nor one the browser owns — the rules
  // and the list are txn/actionShortcuts.json, enforced by the designer, the
  // lint / save and, for a hand-edited model, ignored at runtime. System
  // actions take no key of their own (theirs are fixed). Presentation only.
  shortcut?: string
  // system_op='export': what the Export control offers. The server re-reads it
  // (twasta_runtime_engine.export.model.export_action_of). See
  // ExportActionSettings.
  export?: ExportActionSettings
  // Legacy carrier — the business-logic / Call-API call-string
  // (business_logic('fn','desc','lang') or rest_service('url')). Still read for
  // back-compat; new edits write the structured fields above. Kept in sync as a
  // derived value so the engine + legacy readers keep working.
  logic?: string
  // Pre-action validations — identical structure to a form's validations.
  validations?: Validation[]
  // Row-scoped actions only: an expression (same language as a validation's
  // ``expression``) deciding whether this action appears in a given row's menu.
  // The engine evaluates it per list row and reports the falsy ones as the
  // row's ``_hidden_actions``; a row where it is falsy simply doesn't offer the
  // button. Absent ⇒ always shown.
  //
  // PRESENTATION ONLY — it takes an action out of a menu where it could not
  // succeed. What refuses the action is its ``validations``, which run
  // server-side on every call, so a condition that matters belongs in BOTH.
  visible_when?: string
  // Ask the user for ONE value before the action runs (a scanned code or a
  // typed one) and post it with the form values. See ActionCapture.
  capture?: ActionCapture
  // Designer-only OFF switch (mirrors ValidationRule.disabled). Not emitted
  // by the LLM. The runtime hides/ignores a disabled action.
  disabled?: boolean
  // 1-based render position, stamped by the designer's drag-drop reorder.
  // All runtimes render actions sorted by this (see sortActionsByOrder);
  // entries without one keep their array position, so legacy metadata is
  // unaffected.
  order?: number
  // Optional confirmation the user must clear AFTER the pre-action validations
  // pass and BEFORE the action's effect runs (see ActionConfirm). Absent / mode
  // 'none' = no confirmation.
  confirmation?: ActionConfirm
  // Whether the list view may run this action against MANY selected records at
  // once (the bulk-action bar). Custom handlers default ON — set to ``false``
  // in the designer to hide it from bulk (the action then runs one record at a
  // time only). A SYSTEM ``delete`` defaults OFF — set to ``true`` to let users
  // tick several rows and delete them together. Every other system op (add /
  // edit / view / save) and select_grid / report / assistant / import are never
  // bulk-able (inherently single-record / interactive). See
  // bulkEligibleActions() and the engine's ``action_bulk_allowed``.
  allow_bulk?: boolean
  // An action button imported from a Vision application's OBJ_ACTIONS row.
  // See VisionActionSource.
  vision?: VisionActionSource
}

// Where an imported Vision button came from: an OBJ_ACTIONS row (+ its
// SYSTEM_EVENTS bean) or, with ``kind: 'link'``, an OBJ_LINKS row.
// The importer writes it; the screen reads ``builtin`` to perform Add / Edit /
// Audit Trail itself, and shows every other one as not yet runnable in Twasta
// (its bean is named in ``event``, a link's target in ``link``).
// ``obj_actions`` / ``obj_links`` is the original row, verbatim, so a round
// trip back to Vision can restore it exactly.
export interface VisionActionSource {
  title: string
  form_no?: string
  line_no?: number | string
  kind?: 'action' | 'link'
  /** OBJ_ACTIONS.SERVICE_CODE — the Vision EVENT code (e.g. pre_confirm). */
  service_code?: string
  builtin?: 'add' | 'edit' | 'audit_trail'
  event?: { service?: string; comp_type?: string; component?: string; method?: string }
  /** OBJ_LINKS only: what the link opens (TARGET_OBJ_NAME, LINK_ARG, …). */
  link?: { target_object?: string; link_form_name?: string; link_uri?: string;
    link_type?: string; link_arg?: string; link_name?: string }
  obj_actions?: Record<string, unknown>
  obj_links?: Record<string, unknown>
}

/** The Vision row a button was imported from — OBJ_ACTIONS or OBJ_LINKS; both
 *  carry the DISPLAY_MODE and SHOW_CONFIRM the screen gates the button by. */
function visionRowOf(a: Action | null | undefined): Record<string, unknown> | undefined {
  const v = visionActionOf(a)
  return v?.obj_actions ?? v?.obj_links
}

/** An imported Vision button with OBJ_ACTIONS.SERVICE_HANDLER 3 (despatch's
 *  Default, Lot No): Vision's ServiceHandlerServlet3 writes the bean's
 *  returned <Detail> rows straight into the form — no pick list. (Handler 4 is
 *  the one that opens a popup to pick rows, then calls ``<event>_transform``.) */
export function visionFillsDirectly(a: Action | null | undefined): boolean {
  const row = visionActionOf(a)?.obj_actions
  return String(row?.service_handler ?? '').trim() === '3'
}

/** An imported Vision LINE button with OBJ_ACTIONS.PAGE_CONTEXT 1 (adj_iss /
 *  despatch Stock, Default, PackList): Vision shows it on the line form's LIST
 *  page (page context 1; 2 is the single-record form) and runs it on the line
 *  selected there — so it also belongs on the line grid's toolbar. */
export function visionOnLineList(a: Action | null | undefined): boolean {
  const row = visionActionOf(a)?.obj_actions
  return String(row?.page_context ?? '').trim() === '1'
}

/** An imported Vision button with OBJ_ACTIONS.SERVICE_HANDLER 'D' — "Delete
 *  All" on a line form (sorder, adj_iss, porder…). Vision runs no bean for it:
 *  the client (E12CommonUtils.deleteAllDetailLine) deletes EVERY line of the
 *  form the way a single line's delete does (MasterStatefulEJB.applyDataToXML
 *  with editFlag D): a saved line is flagged deleted and goes on Save, a line
 *  not saved yet is dropped. No confirmation (IS_CONFIRMATION_REQ is blank). */
export function visionDeletesAllLines(a: Action | null | undefined): boolean {
  const row = visionActionOf(a)?.obj_actions
  return String(row?.service_handler ?? '').trim().toUpperCase() === 'D'
}

/** An imported Vision button with OBJ_ACTIONS.SERVICE_HANDLER 4 (despatch's
 *  Stock, PackList, Alloc Item): its rows are a pick list — the user ticks
 *  some, and those go back (run_action ``selected_rows``) through the
 *  button's ``<event>_transform`` bean, whose rows are the lines. */
export function visionPicksThenTransforms(a: Action | null | undefined): boolean {
  const row = visionActionOf(a)?.obj_actions
  return String(row?.service_handler ?? '').trim() === '4'
}

/** An imported Vision button whose bean the runtime calls (the importer bound
 *  its SYSTEM_EVENTS class + SYSTEM_EVENT_SERVICES method as business_logic). */
export function visionRunnable(a: Action | null | undefined): boolean {
  if (!a || typeof a !== 'object') return false
  // An OBJ_LINKS button the importer mapped to an 'open' binding (LINK_TYPE
  // 1 / 2 / 4 / 5 / 6) runs like any other open button.
  if ((a as ActionDef).handler === 'open' && (a as ActionDef).open?.obj_name) return true
  // An Email button (OBJ_ACTIONS SERVICE_HANDLER 6) opens the send window.
  if ((a as ActionDef).handler === 'send') return true
  const bl = (a as ActionDef & { business_logic?: { vision_method?: string } }).business_logic
  return !!(bl && typeof bl === 'object' && bl.vision_method)
}

/** The Vision source of an action, when it was imported from OBJ_ACTIONS. */
export function visionActionOf(a: Action | null | undefined): VisionActionSource | undefined {
  if (!a || typeof a !== 'object') return undefined
  const v = (a as ActionDef).vision
  return v && typeof v === 'object' ? v : undefined
}

/** Where a record-level button can appear: the list (Vision's "home screen" —
 *  the ⋮ row menu) or an open record in one of the form modes. */
export type ActionSurface = 'home' | 'view' | 'edit' | 'add'

/** Vision's OBJ_ACTIONS / OBJ_LINKS DISPLAY_MODE: the surfaces a button shows on.
 *    1 home only · 2 home + view · 3 home + edit · 4 home + add + edit
 *  0, null (and any other value) show everywhere, as Vision does. */
const VISION_DISPLAY_MODES: Record<string, readonly ActionSurface[]> = {
  '1': ['home'],
  '2': ['home', 'view'],
  '3': ['home', 'edit'],
  '4': ['home', 'add', 'edit'],
}

/** OBJ_LINKS departs from OBJ_ACTIONS in two ways:
 *  - a link whose FIELD_NAME is ``home_…`` (Supplier's Ledger, Upload Utility,
 *    Gstin Details, Entity Alias) is a HOME-screen link — never on the form;
 *  - a DISPLAY_MODE 1 link (Account / Cost Center / Credit Term / Site Code
 *    Details, Supplier Data) shows on Add as well as the home screen. */
const VISION_LINK_DISPLAY_MODES: Record<string, readonly ActionSurface[]> = {
  ...VISION_DISPLAY_MODES,
  '1': ['home', 'add'],
}

/** Whether an action shows on ``surface``. Only imported Vision buttons are
 *  restricted (by their DISPLAY_MODE); every other action is unaffected.
 *  A link on a real field (FIELD_NAME ``ind_no``, ``proj_code``) is never on
 *  the home screen: E12 lists only ``home_…`` links there
 *  (E12TranActionLinks.createActionBtnVPnl). */
export function actionShowsOn(a: Action | null | undefined, surface: ActionSurface): boolean {
  const isLink = visionActionOf(a)?.kind === 'link'
  const row = visionRowOf(a)
  if (isLink) {
    if (String(row?.field_name ?? '').trim().toLowerCase().startsWith('home_')) {
      return surface === 'home'
    }
    if (surface === 'home') return false
  }
  const modes = isLink ? VISION_LINK_DISPLAY_MODES : VISION_DISPLAY_MODES
  const surfaces = modes[String(row?.display_mode ?? '').trim()]
  return !surfaces || surfaces.includes(surface)
}

/** OBJ_LINKS.SHOW_IN_PANEL values E12 puts in a record's home ⋮ menu. */
const VISION_ROW_MENU_PANELS = new Set(['', '1', '2', '3'])

/** Whether a row-scoped button belongs in a list row's ⋮ menu — Vision's home
 *  screen context menu (E12TranActionLinks.createActionBtnVPnl). Imported
 *  Vision buttons follow E12:
 *  - a link only when it is a home link (see actionShowsOn) with SHOW_IN_PANEL
 *    blank / 1 / 2 / 3;
 *  - REC_SPECIFIC 1 (action or link) belongs to the screen's "More" menu
 *    beside Add New, not to any one record.
 *  Every other action shows. */
export function visionInRowMenu(a: Action | null | undefined): boolean {
  const v = visionActionOf(a)
  if (!v) return true
  const row = visionRowOf(a)
  if (String(row?.rec_specific ?? '').trim() === '1') return false
  if (v.kind !== 'link') return true
  return actionShowsOn(a, 'home')
    && VISION_ROW_MENU_PANELS.has(String(row?.show_in_panel ?? '').trim())
}

/** Vision's SHOW_CONFIRM is a property of a button's TITLE, not of one row:
 *  E12 keeps one map keyed by the title, filled in LINE_NO order with only the
 *  non-blank codes, and judges every button of that title by it. So two
 *  "Edit" rows — line 2 blank, line 3 ``000`` — are both hidden on a
 *  confirmed record. Pass the result to showConfirmAllows. */
export function visionShowConfirmByTitle(actions: readonly Action[] | null | undefined): Map<string, string> {
  const rows = (actions || [])
    .map((a, idx) => ({ a, idx, row: visionRowOf(a) }))
    .filter(x => x.row)
    .sort((x, y) => (Number(x.row?.line_no) || 0) - (Number(y.row?.line_no) || 0) || x.idx - y.idx)
  const out = new Map<string, string>()
  for (const { a, row } of rows) {
    const code = String(row?.show_confirm ?? '').trim()
    if (code) out.set(String(legacyActionToDef(a).name || '').trim(), code)
  }
  return out
}

/** Whether an imported Vision screen offers a Delete: E12's home menu has one
 *  only when OBJ_ACTIONS carries a "Delete" row. ``undefined`` when the screen
 *  has no imported Vision buttons — the platform's own rule then applies. */
export function visionScreenOffersDelete(actions: readonly Action[] | null | undefined): boolean | undefined {
  const vision = (actions || []).filter(a => visionActionOf(a))
  if (vision.length === 0) return undefined
  return vision.some(a => visionActionOf(a)?.kind !== 'link'
    && String(legacyActionToDef(a).name || '').trim().toLowerCase() === 'delete')
}

/** The three Vision record states, in OBJ_ACTIONS.SHOW_CONFIRM's char order. */
/** A detail form whose rows are CANDIDATES the user ticks. Business logic
 *  (a header item_change) supplies the candidates; the grid shows a tick box
 *  per row bound to `column` ('Y' / 'N') and no Add / Edit / Delete; on save an
 *  unticked new row is not sent and an unticked saved row is deleted.
 *  - `key`: the column that identifies a candidate (e.g. asset_code) — a
 *    candidate matching a row already on the form is not added twice, and a
 *    `replace` never drops a saved row.
 *  - `refill_on_open`: a header column whose item_change is re-run when a
 *    saved record opens (and after each save), so the candidates that were not
 *    ticked are listed again, unticked, beside the saved (ticked) rows. */
export interface RowSelectConfig {
  column: string
  key?: string
  refill_on_open?: string
  /** Candidates offered while the record is NEW arrive ticked (the user
   *  unticks what to leave out). Once saved, new candidates arrive unticked
   *  so a saved selection is shown as it was made. */
  default_ticked?: boolean
}

export interface RowSelect {
  column: string
  key: string | null
  refillOnOpen: string | null
  defaultTicked: boolean
}

export function rowSelectOf(sm: SqlModel | null | undefined): RowSelect | null {
  const rs = sm?.row_select
  const column = String(rs?.column || '').trim().toLowerCase()
  if (!column) return null
  const key = String(rs?.key || '').trim().toLowerCase()
  const refill = String(rs?.refill_on_open || '').trim().toLowerCase()
  return { column, key: key || null, refillOnOpen: refill || null, defaultTicked: rs?.default_ticked === true }
}

/** A row counts as ticked unless its tick column says 'N' — so rows added
 *  any other way (a picker, an import) are kept. */
export function rowIsTicked(row: Record<string, unknown>, rs: RowSelect): boolean {
  return String(row[rs.column] ?? '').trim().toUpperCase() !== 'N'
}

/** A detail form the user may not add to, edit or delete from on screen:
 *  `read_only` (reference rows, writes refused), `lines_locked` (rows that
 *  business logic fills and the record saves) or `row_select` (ticked only). */
export function formLinesViewOnly(sm: SqlModel | null | undefined): boolean {
  return !!(sm?.read_only || sm?.lines_locked || rowSelectOf(sm))
}

export type VisionRecordState = 'confirm' | 'cancel' | 'close'
const VISION_STATE_ORDER: readonly VisionRecordState[] = ['confirm', 'cancel', 'close']
/** Column (db_name) + value that put a record in each state. */
export type VisionStates = Partial<Record<VisionRecordState, { col: string; val: string }>>

/** TRANSETUP's workflow for an imported Vision screen. */
export interface VisionWorkflow {
  opt?: string
  obj_name?: string
  process_id?: string
}

/** A record's states (see visionRecordStates) plus ``workflow`` — the
 *  record's workflow is in progress (no Edit / Submit). */
export type VisionRecordStateMap = Partial<Record<VisionRecordState, boolean>> & {
  workflow?: boolean
}

/** Which states a record is in, from its values (keyed by form key or
 *  db_name, any case). A state the screen does not define stays undefined —
 *  unknown, so it never hides anything. CHAR values are compared trimmed.
 *  ``saved`` false (a record being added) has no workflow yet;
 *  ``inWorkflow`` is the engine's load verdict — absent, a list row's own
 *  ``_wf_in_progress`` is read. */
export function visionRecordStates(
  sm: SqlModel | null | undefined,
  values: Record<string, unknown> | null | undefined,
  opts: { saved?: boolean; inWorkflow?: boolean } = {},
): VisionRecordStateMap {
  const out: VisionRecordStateMap = {}
  if (sm?.list_view?.vision_workflow && opts.saved !== false
      && (opts.inWorkflow ?? values?._wf_in_progress === true)) {
    out.workflow = true
  }
  const states = sm?.list_view?.vision_states
  if (!states || !values) return out
  const lower: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(values)) lower[k.toLowerCase()] = v
  // Values may be keyed by db_name or by the form key (the column's name,
  // spaces → '_', as columnFormKey).
  const aliases = (dbName: string): string[] => {
    const c = (sm?.columns || []).map(w => w.column)
      .find(col => (col?.db_name || '').toLowerCase() === dbName)
    return [dbName, (c?.name || '').trim().replace(/\s+/g, '_').toLowerCase()]
  }
  for (const st of VISION_STATE_ORDER) {
    const rule = states[st]
    if (!rule?.col) continue
    const key = aliases(rule.col.toLowerCase()).find(k => k && k in lower)
    const v = key ? lower[key] : undefined
    out[st] = String(v ?? '').trim() === String(rule.val ?? '').trim()
  }
  return out
}

/** Vision's OBJ_ACTIONS / OBJ_LINKS SHOW_CONFIRM — 3 chars for confirm / cancel / close:
 *  '0' hide the button while the record IS in that state, '1' show it ONLY
 *  while it is, '9' (or blank / anything else) ignore that state. Only
 *  imported Vision buttons are affected. A record whose workflow is in
 *  progress also hides Edit and Submit (see workflowAllows). ``byTitle``
 *  (visionShowConfirmByTitle over the same form's buttons) gives the code
 *  E12 actually applies — the title's, not this row's own. */
export function showConfirmAllows(
  a: Action | null | undefined,
  inState: VisionRecordStateMap,
  byTitle?: ReadonlyMap<string, string>,
): boolean {
  if (inState.workflow && !workflowAllows(a)) return false
  const own = String(visionRowOf(a)?.show_confirm ?? '')
  const code = byTitle && visionActionOf(a)
    ? (byTitle.get(String(legacyActionToDef(a as Action).name || '').trim()) ?? '')
    : own
  return VISION_STATE_ORDER.every((st, i) => {
    const known = inState[st]
    if (known === undefined) return true
    if (code[i] === '0') return !known
    if (code[i] === '1') return known
    return true
  })
}

/** Whether an imported Vision button may show while the record's workflow is
 *  in progress: Edit and Submit may not. Matched by the button's Vision title. */
function workflowAllows(a: Action | null | undefined): boolean {
  const v = visionActionOf(a)
  if (!v) return true
  const title = String(v.title || (a as ActionDef).name || '').trim().toLowerCase()
  return !(v.builtin === 'edit' || title === 'edit' || title === 'submit')
}

/** What the screen says when an imported Vision button that Twasta cannot run
 *  yet is clicked. */
export function visionNotWiredMessage(name: string, v: VisionActionSource): string {
  if (v.kind === 'link') {
    const target = v.link?.target_object || v.link?.link_uri || ''
    return `"${name}" is not available in Twasta yet`
      + (target ? ` (Vision link: ${target}).` : '.')
  }
  const bean = v.event?.component
    ? `${v.event.component}${v.event.method ? `.${v.event.method}` : ''}`
    : (v.service_code || '')
  return `"${name}" is not available in Twasta yet`
    + (bean ? ` (Vision: ${bean}).` : '.')
}

// How strongly a user must confirm an action before it runs:
//   - 'none'      no confirmation (default)
//   - 'reconfirm' a simple "are you sure?" yes/no question
//   - 'phrase'    the user must type an expected phrase exactly (e.g. the
//                 transaction id, or a fixed word like DELETE)
//   - 'otp'       a 6-digit one-time code emailed to the user, typed back
//   - 'password'  the user re-enters their own account password
// otp + password are verified SERVER-side (the engine emails/stores the code;
// the host verifies the password); phrase is matched server-side against the
// resolved template; reconfirm is a client acknowledgement.
export type ConfirmMode = 'none' | 'reconfirm' | 'phrase' | 'otp' | 'password'

export interface ActionConfirm {
  mode: ConfirmMode
  // The confirmation prompt shown to the user. Supports {form.X} / {header.X}
  // interpolation — the SAME convention validation messages use — so the
  // message can name the record, e.g.
  // "Are you sure you want to edit customer {form.customer_name}?"
  message?: string
  // mode='phrase' only — the expected phrase the user must type. A template
  // using {form.X} / {header.X} (e.g. "{form.txn_id}") or a literal
  // (e.g. "DELETE"); resolved + compared server-side (case-insensitive, trimmed).
  phrase?: string
  // mode='phrase' only — a short label for what to type, shown by the input
  // (e.g. "transaction id").
  phrase_label?: string
}

export const CONFIRM_MODE_LABELS: Record<ConfirmMode, string> = {
  none: 'No confirmation',
  reconfirm: 'Ask a yes/no question',
  phrase: 'Type a phrase to confirm',
  otp: 'Email a one-time code',
  password: 'Re-enter account password',
}

// An actions[] entry: a legacy string or a structured ActionDef.
export type Action = string | ActionDef

export const ACTION_HANDLER_LABELS: Record<ActionHandlerKind, string> = {
  // The designer's one word for each (the redesigned Logic / button editor).
  system: 'Built-in',
  html_modal: 'Show HTML',
  message: 'Message',
  item_change: 'Fill fields',
  select_grid: 'Pick rows',
  report: 'Report',
  assistant: 'Assistant',
  payment: 'Payment',
  import: 'Import file',
  open: 'Open object',
  wizard: 'Wizard',
  send: 'Send message',
}

// handler='assistant' — how the button calls the in-app Assistant.
export interface AssistantActionBinding {
  // 'intent' (default): run the named DEFINED INTENT deterministically —
  // no LLM turn; the intent's fulfillment (visual / report / SQL / action
  // proposal / …) renders in a modal. 'prompt': open the Assistant chat
  // seeded with the rendered prompt (needs the app's AI configured).
  mode?: 'intent' | 'prompt'
  // mode 'intent': the intent_id defined in Configure AI → Intents.
  intent_id?: string
  // mode 'intent': intent parameter -> value template. "{form.COL}" /
  // "{header.COL}" whole-value tokens keep the raw type; embedded tokens
  // interpolate as text; anything else passes through as a literal.
  param_map?: Record<string, string>
  // mode 'prompt': the question template ({form.COL} / {header.COL} tokens).
  prompt?: string
  // Optional modal title (defaults to the action name).
  title?: string
}

// handler='payment' — what the application charges for when this button is
// pressed.
//
// EVERY FIELD IS A COLUMN NAME, and that is the whole security design. The
// binding is authored in the designer, echoed to the browser by the engine
// and posted back with the checkout request, so anything in it is something
// the browser could have altered. Column names are safe to alter: the server
// re-reads the record and takes the amount from `amount_column` itself, so
// the worst a tampered binding can do is name a column that does not hold an
// amount, and the order is then refused.
export interface PaymentActionBinding {
  // REQUIRED. The column holding what to charge, in major units (3200.50).
  amount_column: string
  // Fixed currency, or the column holding it. Neither set ⇒ the gateway's
  // configured currency.
  currency?: string
  currency_column?: string
  // The human reference on the gateway's receipt (an order number). Falls
  // back to the record's id.
  reference_column?: string
  // WHO MAY PAY. The column naming the customer this record belongs to; it
  // must equal the caller's own identity. Leave it unset and the button works
  // only for signed-in users of the application — a storefront where
  // customers pay for their own orders MUST set it.
  owner_column?: string
  // Prefill for the gateway's own form.
  payer_name_column?: string
  payer_email_column?: string
  payer_contact_column?: string
  // Values of the write-back status column that already mean "settled"; a
  // second order for such a record is refused. Default ['PAID'].
  paid_values?: string[]
  // What to stamp on the record once the money is captured. Everything is
  // optional — a record that needs no stamp is written back as complete.
  writeback?: {
    status_column?: string
    status_value?: string
    payment_id_column?: string
    paid_amount_column?: string
    paid_at_column?: string
    method_column?: string
  }
  // Cash on delivery: the status value stamped on the record (in
  // `writeback.status_column`, with 'COD' in `writeback.method_column`) when
  // the customer chooses to pay at the door. Unset ⇒ COD is not offered for
  // this action. Read by the server FROM THE MODEL — the copy echoed here
  // only tells the surface to show the option.
  cod_status_value?: string
  // Modal title and the line shown once it succeeds.
  title?: string
  success_message?: string
}

// What a dispatched 'payment' action hands the host surface to open. The host
// owns the gateway SDK (it differs per surface: the platform's Run view, the
// published site and the downloaded runtime all load it differently), so the
// txn layer only says WHAT to charge for, never how to display a card form.
export interface PaymentActionRequest {
  title: string
  objName: string
  txnId: string
  binding: PaymentActionBinding
  successMessage?: string
}

// handler='import' — a file import into the object the button sits on.
// The server re-reads this from the model by the action's name, so what the
// browser holds is presentation only.
export interface ImportActionBinding {
  // 'add' new records, 'edit' existing ones (matched by key), or 'upsert'
  // (default): add or update depending on whether the key exists.
  mode?: 'add' | 'edit' | 'upsert'
  // The person may not change the mode in the map step.
  lock_mode?: boolean
  // Window title (defaults to the button's name).
  title?: string
}

// system_op='export' — the Export system action's settings. Added by default
// to every new transaction (unless the specification says its records must not
// be exported). Who may export is the object's `export` right; organizations'
// own templates live in the tenant DB, the STANDARD ones here.
export interface ExportActionSettings {
  places?: ExportPlace[]
  formats?: ExportFormat[]
  // A lower cap than the hosting plan's `export.max_rows` (never higher).
  max_rows?: number
  // Offer "As on screen" (the person's own list columns, no template).
  offer_list_view?: boolean
  // Let each organization's functional admin make its own templates.
  org_templates?: boolean
  // Ask "why are you exporting these records?" (kept in the audit entry).
  require_reason?: boolean
  // Standard templates, shipped to every organization (read-only for them).
  templates?: ExportTemplate[]
}

// What a file import reports when its window closes (mirrors the Assistant's
// ImportOutcome).
export interface ImportActionOutcome {
  total: number
  saved: number
  failed: number
  pending: number
  unchanged?: number
  leftOut?: number
}

// What a dispatched 'import' action hands the host surface to open.
export interface ImportActionRequest {
  title: string
  objName: string
  actionName: string
  binding: ImportActionBinding
  // Called when the window closes; ``null`` when nothing was imported.
  onClosed?: (outcome: ImportActionOutcome | null) => void
}

// handler='open' — open ANOTHER object with values from the clicked row.
//
// Every value is a TEMPLATE resolved in the browser against the clicked row:
// "{form.COL}" (the row / open form), "{header.COL}" (its header form — on a
// list row the same row), embedded tokens interpolate as text, anything else
// is a literal. The values become what the user would otherwise type on the
// target, so they grant nothing: the target's own rights, item_change,
// validations, approvals and save all apply, and nothing is saved for them.
// 'U' — a Transaction Wizard: its ``params`` are the wizard's launch inputs.
export type OpenObjectType = 'T' | 'G' | 'D' | 'V' | 'R' | 'U'

// How a transaction target opens:
//  - 'add'          a new record;
//  - 'edit'         the ONE record matching ``match`` (none → a message; more
//                   than one → its list, filtered to the matches);
//  - 'edit_or_add'  that record when exactly one matches, a new record when
//                   none does, the filtered list when several do;
//  - 'view'         the ONE record matching ``match``, read-only (``set`` and
//                   ``then`` do not apply).
export type OpenActionMode = 'add' | 'edit' | 'edit_or_add' | 'view'

// One column to set on the target's header form, in order.
export interface OpenActionSetEntry {
  // The target column (logical name, db_name or form key).
  column: string
  // A template ("{form.COL}" / "{header.COL}" / literal).
  value: unknown
  // Fire the column's item_change after setting it, exactly as if the user
  // had typed the value — and wait for it before the next column. Default true.
  item_change?: boolean
  // Lock the field on screen after setting it (presentation only).
  protect?: boolean
}

// A step run on the target after every ``set`` value has landed.
//  - 'line_command'  a Line Entry command on a line form (``form_no``);
//                    ``select: 'all'`` adds every candidate line with no
//                    picker, 'ask' opens the command's normal chooser.
//  - 'action'        a named action button of the target form (form_no, or
//                    the header), applied like a click on it.
export interface OpenActionThenStep {
  run: 'line_command' | 'action'
  form_no?: string
  command?: string
  select?: 'all' | 'ask'
  action_name?: string
}

// A criterion / filter / prompt value for a G / D / V / R target: a template,
// or {value, protect} to lock it on the target's criteria form.
export type OpenActionParam = unknown | { value: unknown; protect?: boolean }

export interface OpenActionBinding {
  obj_type?: OpenObjectType
  obj_name: string
  // T only.
  mode?: OpenActionMode
  // T, mode edit / edit_or_add / view: target COLUMN → template identifying the
  // record to open.
  match?: Record<string, unknown>
  // T only, in order.
  set?: OpenActionSetEntry[]
  // T only, in order, after ``set``.
  then?: OpenActionThenStep[]
  // G / D / V / R: criterion / filter / prompt NAME → value.
  params?: Record<string, OpenActionParam>
  // 'navigate' (default) takes the user to the target's own screen; 'drawer'
  // opens it in a slide-over above this screen, which refreshes when it
  // closes. A Report always opens in its viewer window.
  presentation?: 'navigate' | 'drawer'
  // Title of the drawer / the opened screen's banner (default: button name).
  title?: string
}

// What a resolved 'open' action carries to the target — serialisable (it may
// travel through the open hand-off store to a freshly mounted screen).
export interface OpenScreenPayload {
  title: string
  actionName: string
  sourceObjName: string
  objType: OpenObjectType
  objName: string
  mode: OpenActionMode
  // Resolved match values; ``matchMissing`` names a match column whose
  // template could not be resolved from the row — the target then refuses to
  // look a record up rather than match on fewer columns than were designed.
  match: Record<string, unknown>
  matchMissing: string[]
  set: Array<{ column: string; value: unknown; itemChange: boolean; protect: boolean }>
  then: OpenActionThenStep[]
  params: Record<string, unknown>
  protectedParams: string[]
  presentation: 'navigate' | 'drawer'
}

// What a dispatched 'open' action hands the host surface.
export interface OpenActionRequest extends OpenScreenPayload {
  // Drawer only: called when the drawer closes, so the source screen can
  // refresh what the target may have changed.
  onClosed?: () => void
}

// What a dispatched 'assistant' action hands the host surface to open.
export interface AssistantActionRequest {
  title: string
  mode: 'intent' | 'prompt'
  intentId?: string
  parameters?: Record<string, unknown>
  prompt?: string
}

// handler='wizard' — start a Transaction Wizard (obj_type "U") with launch
// inputs taken from the clicked row. ``param_map`` maps the wizard's INPUT
// names to templates in the 'assistant' grammar ("{form.COL}" / "{header.COL}"
// whole tokens keep the raw type, embedded ones interpolate, anything else is a
// literal). The inputs grant nothing: the wizard's own grant, steps and the
// base transaction's rules decide what happens.
export interface WizardActionBinding {
  obj_name: string
  param_map?: Record<string, unknown>
  // 'navigate' (default) opens the wizard on its own screen; 'drawer' opens it
  // in a slide-over above this one, which refreshes when it closes.
  presentation?: 'navigate' | 'drawer'
  // Drawer / banner title (default: the button's name).
  title?: string
}

// What a dispatched 'wizard' action hands the host surface to open.
export interface WizardActionRequest {
  title: string
  objName: string
  inputs: Record<string, unknown>
  presentation: 'navigate' | 'drawer'
  // Drawer only: called when it closes, so the source screen can refresh.
  onClosed?: () => void
  // The OPEN record editor, for a `fill` wizard (mode "fill"): the buffer it
  // starts from and where its result goes back (WizardRunnerProps.seed /
  // onFill). Set by the editor's buttons; a host passes both to the runner,
  // which uses them only when the wizard's mode is fill. Drawer only — a
  // navigation would leave the buffer behind.
  fill?: {
    seed: { header: Record<string, unknown>; details: Record<string, Array<Record<string, unknown>>> }
    onFill: (result: { header: Record<string, unknown>; lines: Record<string, Array<Record<string, unknown>>> }) => void
  }
}

// Build the host-facing request for a clicked 'wizard' action, or a string
// error naming what is misconfigured.
export function buildWizardActionRequest(
  actionName: string,
  binding: WizardActionBinding | undefined,
  formValues: Record<string, unknown> | undefined,
  headerValues: Record<string, unknown> | undefined,
  onClosed?: () => void,
): WizardActionRequest | string {
  const b = binding || ({} as WizardActionBinding)
  const objName = String(b.obj_name || '').trim()
  if (!objName) return `Action "${actionName}" has no wizard configured.`
  const map: Record<string, string> = {}
  const literals: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(b.param_map || {})) {
    if (typeof v === 'string') map[k] = v
    else if (v !== undefined && v !== null) literals[k] = v
  }
  return {
    title: (b.title || '').trim() || actionName,
    objName,
    inputs: { ...literals, ...resolveAssistantActionParams(map, formValues, headerValues) },
    presentation: b.presentation === 'drawer' ? 'drawer' : 'navigate',
    onClosed,
  }
}

export function isActionDef(a: Action | null | undefined): a is ActionDef {
  return !!a && typeof a === 'object' && typeof (a as ActionDef).name === 'string'
}

// The display name for an action (works for both legacy strings and ActionDef).
export function actionName(a: Action): string {
  if (isActionDef(a)) return a.name || ''
  const str = (a || '').toString()
  const colon = str.indexOf(':')
  return colon === -1 ? str : str.slice(0, colon)
}

// The logic call-string for an action (the part after the first colon for a
// legacy string; the structured business_logic / rest_api re-serialised, else
// ``logic`` for an ActionDef).
export function actionLogic(a: Action): string {
  if (isActionDef(a)) {
    if (a.business_logic?.method) return businessLogicCallString(a.business_logic)
    if (a.rest_api?.url) return `rest_service('${a.rest_api.url}')`
    return a.logic || ''
  }
  const str = (a || '').toString()
  const colon = str.indexOf(':')
  return colon === -1 ? '' : str.slice(colon + 1)
}

// Upgrade a legacy `"name:logic"` string (or a structured ActionDef whose logic
// is still a call-string) to a normalised ActionDef carrying the structured
// business_logic / rest_api fields. Today's legacy actions carry a
// business_logic(...) call, so item_change is the faithful default handler. An
// empty/unparseable string still yields a usable def so the editor never loses
// the row.
export function legacyActionToDef(a: Action): ActionDef {
  if (isActionDef(a)) {
    // Already structured — nothing to upgrade.
    if (a.business_logic || a.rest_api) return a
    // Structured def but logic still a call-string: lift it into the object.
    const fromStr = legacyItemChangeToMode(a.logic || '')
    if (fromStr?.mode === 'business_logic') return { ...a, business_logic: fromStr.business_logic }
    if (fromStr?.mode === 'rest_api') return { ...a, rest_api: fromStr.rest_api }
    return a
  }
  const def: ActionDef = {
    name: actionName(a),
    handler: 'item_change',
    validations: [],
  }
  const fromStr = legacyItemChangeToMode(actionLogic(a))
  if (fromStr?.mode === 'business_logic') def.business_logic = fromStr.business_logic
  else if (fromStr?.mode === 'rest_api') def.rest_api = fromStr.rest_api
  else def.logic = actionLogic(a)
  return def
}

// One-line summary for the property-panel row.
export function actionSummary(a: Action): string {
  const def = legacyActionToDef(a)
  const label = ACTION_HANDLER_LABELS[def.handler] || def.handler
  const detail = def.handler === 'system'
    ? (def.system_op || 'add')
    : def.handler === 'assistant'
      ? (def.assistant?.mode === 'prompt' ? 'AI prompt' : (def.assistant?.intent_id || 'intent'))
      : def.handler === 'payment'
        ? (def.payment?.amount_column || 'no amount column')
        : def.handler === 'import'
          ? (def.import?.mode === 'add' ? 'add new' : def.import?.mode === 'edit' ? 'update existing' : 'add or update')
          : def.handler === 'open'
            ? openActionSummary(def.open)
            : def.handler === 'wizard'
              ? ((def.wizard?.obj_name || '').trim() || 'no wizard')
              : (def.business_logic?.method || def.rest_api?.url || def.logic || '')
  const vcount = def.validations?.length
    ? ` · ${def.validations.length} validation${def.validations.length === 1 ? '' : 's'}`
    : ''
  const dis = def.disabled ? '(disabled) ' : ''
  const head = def.name || label
  return detail && detail !== head
    ? `${dis}${head} · ${label}: ${detail}${vcount}`
    : `${dis}${head} · ${label}${vcount}`
}

export const ACTION_SCOPE_LABELS: Record<ActionScope, string> = {
  form: 'Whole form (generic)',
  row: 'A specific row',
}

// Resolve an action's scope (form-level vs row-level). An explicit
// ``scope`` set in the designer always wins. Otherwise it is inferred from
// "the nature of the action type":
//   - system actions: edit / delete operate on one existing row → 'row';
//     add / save create or persist the form as a whole → 'form'.
//   - business-logic handlers: select_grid generically inserts new rows into
//     the form → 'form'; item_change / message / html_modal default to acting
//     on a record → 'row'.
export function actionScope(a: Action): ActionScope {
  const def = legacyActionToDef(a)
  if (def.scope === 'form' || def.scope === 'row') return def.scope
  if (def.handler === 'system') {
    const op = def.system_op || 'add'
    return op === 'edit' || op === 'delete' || op === 'view' ? 'row' : 'form'
  }
  if (def.handler === 'select_grid' || def.handler === 'import') return 'form'
  return 'row'
}

// Render order for an actions list: sorted by the designer-stamped ``order``
// (1-based). Entries without one keep their array position (legacy metadata
// or LLM-authored actions), so an unstamped list is returned unchanged.
// Every runtime surface renders actions through this.
export function sortActionsByOrder(actions: Action[] | undefined): Action[] {
  const list = actions || []
  // An action WITHOUT an explicit order keeps the place it was defined in,
  // relative to the ordered ones around it: it inherits the position of the
  // last action that did carry a number, and ties break by definition index.
  // Scoring it by its own index instead would send every unnumbered action to
  // the front of a model where somebody numbered its neighbours 10, 20, 30 —
  // reordering buttons the designer never touched.
  let carried = 0
  return list
    .map((a, idx) => {
      const own = isActionDef(a) && typeof a.order === 'number' ? a.order : null
      if (own !== null) carried = own
      return { a, key: own === null ? carried : own, idx }
    })
    .sort((x, y) => (x.key - y.key) || (x.idx - y.idx))
    .map(x => x.a)
}

// System actions for ops that already have a built-in control on the list
// screen don't render as separate buttons — that would duplicate the
// built-in. Instead they MERGE into it:
//   - system_op 'add'  → the built-in "Add" button takes the action's name
//     as its label;
//   - system_op 'edit' → the action's name becomes the built-in Edit
//     control's hover (tooltip) text.
//   - system_op 'view' → the action's name becomes the built-in View
//     control's hover text (the control shown when a record is read-only).
// In all cases clicking the built-in control routes through ``run_action``
// so the action's PRE-action validations run (blocking on failure) before
// the operation. ``isMergedSystemAction`` is the shared predicate every
// surface uses to decide an action merges rather than renders.
export interface SystemActionOverrides {
  add?: ActionDef
  edit?: ActionDef
  // Conditions/validations that apply when OPENING a record for viewing.
  view?: ActionDef
  // A system 'delete' action merges into the LIST's built-in Delete (row menu
  // label + pre-action validations) rather than rendering a second Delete
  // beside it. Unlike add/edit/view it still shows in the open-record action
  // bar — the editor has no built-in Delete of its own — so it is NOT an
  // ``isMergedSystemAction``; only partitionActionsByScope diverts it.
  delete?: ActionDef
}

export function isMergedSystemAction(def: ActionDef): boolean {
  if (def.handler !== 'system') return false
  const op = def.system_op || 'add'
  return op === 'add' || op === 'edit' || op === 'view'
}

// Split the (legacy-or-structured) actions list into the visible, enabled,
// named actions for each scope — ready to render in the list toolbar
// (formActions) and the per-row 3-dot menu (rowActions). Disabled / unnamed
// actions are dropped on both sides, mirroring RunActionBar's filter. Both
// sides come back in the designer-specified order.
// System add / edit / view / delete actions are diverted into
// ``systemOverrides`` instead of either list (see above) — the first per op
// wins; later ones are duplicates of the same built-in and are dropped. A
// bulk-capable Delete therefore lives in ``systemOverrides.delete``, so bulk
// callers use ``bulkActionCandidates``.
export function partitionActionsByScope(
  actions: Action[] | undefined,
): { formActions: ActionDef[]; rowActions: ActionDef[]; systemOverrides: SystemActionOverrides } {
  const formActions: ActionDef[] = []
  const rowActions: ActionDef[] = []
  const systemOverrides: SystemActionOverrides = {}
  for (const a of sortActionsByOrder(actions)) {
    const def = legacyActionToDef(a)
    if (def.disabled || !(def.name || '').trim()) continue
    // The Export system action is the screen's own Export control, not a button.
    if (isExportSystemAction(def)) continue
    if (isMergedSystemAction(def) || isSystemDeleteAction(def)) {
      const op = (def.system_op || 'add') as 'add' | 'edit' | 'view' | 'delete'
      if (!systemOverrides[op]) systemOverrides[op] = def
      continue
    }
    if (actionScope(a) === 'row') rowActions.push(def)
    else formActions.push(def)
  }
  return { formActions, rowActions, systemOverrides }
}

// The HEADER form's buttons as the engine sees them: its own `actions[]`
// plus the transaction-level `transaction.actions[]`, which the engine runs
// as header actions (`executor._find_action_def`) and the authoring skill
// documents as "shown on the header form". A form action wins over a
// transaction action of the same name. Returns `sm` itself when there is
// nothing to merge, so memoised consumers see a stable object. Render-only:
// never write the result back into the model.
export function withTransactionActions<T extends { actions?: Action[] }>(
  sm: T | null | undefined, txnActions: Action[] | undefined,
): T | null | undefined {
  if (!sm || !txnActions?.length) return sm
  const own = sm.actions || []
  const names = new Set(own.map(a => (legacyActionToDef(a).name || '').trim().toLowerCase()))
  const extra = txnActions.filter(a => {
    const n = (legacyActionToDef(a).name || '').trim().toLowerCase()
    return n && !names.has(n)
  })
  return extra.length ? { ...sm, actions: [...own, ...extra] } : sm
}

// Where a form-level button is drawn — the ⋮ menu unless the designer put it
// on the bar. One rule for the list toolbar, every list layout and the open
// record's action bar.
export type ActionPlacement = 'bar' | 'menu'
export function actionPlacement(a: { placement?: unknown } | null | undefined): ActionPlacement {
  return a?.placement === 'bar' ? 'bar' : 'menu'
}

// The Export system action — the screen's own Export control (list button,
// selection bar, record ⋮), never a button in any action bar.
export function isExportSystemAction(a: ActionDef): boolean {
  return a.handler === 'system' && a.system_op === 'export'
}

// A system Delete button — the one system op that can run in bulk.
export function isSystemDeleteAction(a: ActionDef): boolean {
  return a.handler === 'system' && a.system_op === 'delete'
}

// Whether ONE action may run against many selected records. MUST agree with
// the engine's ``action_bulk_allowed`` (the server is authoritative):
//   - custom handlers: ON unless ``allow_bulk === false``;
//   - system ``delete``: OFF unless ``allow_bulk === true`` (opt-in);
//   - other system ops (add / edit / view / save) act through the open editor
//     on one record, and select_grid (an interactive pick-rows modal), report
//     (ONE viewer) and assistant (ONE result modal) are single-record: never.
//   - an action with a ``capture`` (one scanned / typed value): never.
//   - send: bulk-ALLOWED — the engine answers ONE send window over every
//     selected record that passed the button's checks (records → groups).
export function actionAllowsBulk(a: ActionDef): boolean {
  if (a.handler === 'system') return isSystemDeleteAction(a) && a.allow_bulk === true
  if (a.handler === 'select_grid' || a.handler === 'report' || a.handler === 'assistant'
    || a.handler === 'import' || a.handler === 'open' || a.handler === 'wizard') {
    return false
  }
  // One scanned / typed value answers for ONE record.
  if (actionCaptureOf(a)) return false
  return a.allow_bulk !== false
}

// The row actions PLUS the merged system Delete — everything the list's bulk
// bar may offer, before the actionAllowsBulk filter.
export function bulkActionCandidates(
  rowActions: ActionDef[] | undefined, overrides: SystemActionOverrides | undefined,
): ActionDef[] {
  return [...(rowActions || []), ...(overrides?.delete ? [overrides.delete] : [])]
}

// The subset of actions a list view may run against MANY selected records at
// once (the bulk-action bar) — see actionAllowsBulk. Pass the row-scoped
// actions (the natural per-record set).
export function bulkEligibleActions(actions: ActionDef[] | undefined): ActionDef[] {
  return (actions || []).filter(actionAllowsBulk)
}

// Resolve a report action's ``param_map`` against the clicked row's values.
// "{form.COL}" (whole-value token) resolves to the row's COL value
// (case-insensitive match, preserving the raw type); embedded tokens inside a
// longer string interpolate as text; anything else passes through literally.
// Unresolvable tokens are dropped so the report's own prompt asks for them.
export function resolveReportActionParams(
  paramMap: Record<string, string> | undefined,
  rowValues: Record<string, unknown> | undefined,
): Record<string, unknown> {
  const byLower: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(rowValues || {})) byLower[k.toLowerCase()] = v
  const out: Record<string, unknown> = {}
  const whole = /^\{form\.([^}]+)\}$/
  for (const [param, raw] of Object.entries(paramMap || {})) {
    const spec = String(raw ?? '')
    const m = whole.exec(spec.trim())
    if (m) {
      const v = byLower[m[1].trim().toLowerCase()]
      if (v !== undefined && v !== null && v !== '') out[param] = v
      continue
    }
    if (spec.includes('{form.')) {
      let missing = false
      const text = spec.replace(/\{form\.([^}]+)\}/g, (_s, col: string) => {
        const v = byLower[col.trim().toLowerCase()]
        if (v === undefined || v === null) { missing = true; return '' }
        return String(v)
      })
      if (!missing) out[param] = text
      continue
    }
    if (spec !== '') out[param] = spec
  }
  return out
}

// ── handler='assistant' template resolution ─────────────────────────────
// Same token convention as report actions, extended with {header.COL} for the
// parent/header form. Whole-value tokens keep the raw type; embedded tokens
// interpolate as text; unresolvable tokens drop the entry (params) or resolve
// to '' (prompt text).
function _assistantTokenValue(
  col: string,
  formLower: Record<string, unknown>,
  headerLower: Record<string, unknown>,
  scope: 'form' | 'header',
): unknown {
  const key = col.trim().toLowerCase()
  return scope === 'header' ? headerLower[key] : formLower[key]
}

const _ASSIST_WHOLE = /^\{(form|header)\.([^}]+)\}$/
const _ASSIST_EMBED = /\{(form|header)\.([^}]+)\}/g

export function resolveAssistantActionParams(
  paramMap: Record<string, string> | undefined,
  formValues: Record<string, unknown> | undefined,
  headerValues: Record<string, unknown> | undefined,
): Record<string, unknown> {
  const formLower: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(formValues || {})) formLower[k.toLowerCase()] = v
  const headerLower: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(headerValues || formValues || {})) headerLower[k.toLowerCase()] = v
  const out: Record<string, unknown> = {}
  for (const [param, raw] of Object.entries(paramMap || {})) {
    const spec = String(raw ?? '')
    const m = _ASSIST_WHOLE.exec(spec.trim())
    if (m) {
      const v = _assistantTokenValue(m[2], formLower, headerLower, m[1] as 'form' | 'header')
      if (v !== undefined && v !== null && v !== '') out[param] = v
      continue
    }
    if (/\{(form|header)\./.test(spec)) {
      let missing = false
      const text = spec.replace(_ASSIST_EMBED, (_s, scope: string, col: string) => {
        const v = _assistantTokenValue(col, formLower, headerLower, scope as 'form' | 'header')
        if (v === undefined || v === null) { missing = true; return '' }
        return String(v)
      })
      if (!missing) out[param] = text
      continue
    }
    if (spec !== '') out[param] = spec
  }
  return out
}

// Render an assistant PROMPT template against the screen's values. Unresolved
// tokens become '' so the question still reads naturally.
export function resolveAssistantPrompt(
  template: string | undefined,
  formValues: Record<string, unknown> | undefined,
  headerValues: Record<string, unknown> | undefined,
): string {
  const formLower: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(formValues || {})) formLower[k.toLowerCase()] = v
  const headerLower: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(headerValues || formValues || {})) headerLower[k.toLowerCase()] = v
  return String(template || '').replace(_ASSIST_EMBED, (_s, scope: string, col: string) => {
    const v = _assistantTokenValue(col, formLower, headerLower, scope as 'form' | 'header')
    return v === undefined || v === null ? '' : String(v)
  }).trim()
}

// Build the host-facing request for a clicked 'assistant' action, or a string
// error naming what is misconfigured.
export function buildAssistantActionRequest(
  actionName: string,
  binding: AssistantActionBinding | undefined,
  formValues: Record<string, unknown> | undefined,
  headerValues: Record<string, unknown> | undefined,
): AssistantActionRequest | string {
  const b = binding || {}
  const mode = b.mode === 'prompt' ? 'prompt' : 'intent'
  const title = (b.title || '').trim() || actionName
  if (mode === 'prompt') {
    const prompt = resolveAssistantPrompt(b.prompt, formValues, headerValues)
    if (!prompt) return `Action "${actionName}" has no assistant prompt configured.`
    return { title, mode, prompt }
  }
  const intentId = (b.intent_id || '').trim()
  if (!intentId) return `Action "${actionName}" has no assistant intent configured.`
  return {
    title, mode, intentId,
    parameters: resolveAssistantActionParams(b.param_map, formValues, headerValues),
  }
}

// Build the host-facing request for a clicked 'payment' action, or a string
// error naming what is misconfigured.
//
// Refuses an unsaved record on purpose. Charging for a form nobody has saved
// would mean either inventing a record after the money lands, or taking money
// against nothing — and the server resolves the amount from the STORED row,
// so there would be nothing to resolve it from.
export function buildPaymentActionRequest(
  actionName: string,
  binding: PaymentActionBinding | undefined,
  objName: string,
  txnId: string | undefined,
): PaymentActionRequest | string {
  const b = binding || ({} as PaymentActionBinding)
  if (!b.amount_column) {
    return `Action "${actionName}" has no amount column configured.`
  }
  if (!txnId) {
    return 'Save this record before taking payment for it.'
  }
  return {
    title: (b.title || '').trim() || actionName,
    objName,
    txnId,
    binding: b,
    successMessage: (b.success_message || '').trim() || undefined,
  }
}

// Build the host-facing request for a clicked 'import' action. Always
// imports into the object the button sits on.
export function buildImportActionRequest(
  actionName: string,
  binding: ImportActionBinding | undefined,
  objName: string,
  onClosed?: (outcome: ImportActionOutcome | null) => void,
): ImportActionRequest {
  const b = binding || {}
  return {
    title: (b.title || '').trim() || actionName,
    objName,
    actionName,
    binding: b,
    onClosed,
  }
}

// ── handler='open' resolution ───────────────────────────────────────────

const OPEN_OBJ_TYPES: readonly OpenObjectType[] = ['T', 'G', 'D', 'V', 'R', 'U']
const OPEN_MODES: readonly OpenActionMode[] = ['add', 'edit', 'edit_or_add', 'view']

export const OPEN_MODE_LABELS: Record<OpenActionMode, string> = {
  add: 'Add a new record',
  edit: 'Edit the matching record',
  edit_or_add: 'Edit if it exists, add otherwise',
  view: 'View the matching record (read-only)',
}

export const OPEN_OBJ_TYPE_LABELS: Record<OpenObjectType, string> = {
  T: 'Transaction',
  G: 'General process',
  D: 'Dashboard',
  V: 'Visual',
  R: 'Report',
  U: 'Wizard',
}

function openActionSummary(b: OpenActionBinding | undefined): string {
  const name = (b?.obj_name || '').trim()
  if (!name) return 'no object'
  const type = OPEN_OBJ_TYPES.includes(b?.obj_type as OpenObjectType) ? b!.obj_type! : 'T'
  if (type !== 'T') return `${OPEN_OBJ_TYPE_LABELS[type]} ${name}`
  const mode = OPEN_MODES.includes(b?.mode as OpenActionMode) ? b!.mode! : 'add'
  return `${name} (${mode.replace(/_/g, ' ')})`
}

// Resolve ONE template against the screen's values. A non-string value is a
// literal (a number, a boolean); a whole "{form.COL}" / "{header.COL}" token
// keeps the column's raw type; embedded tokens interpolate as text. ``ok`` is
// false when a token names a column with no value on the row.
export function resolveActionTemplate(
  spec: unknown,
  formValues: Record<string, unknown> | undefined,
  headerValues: Record<string, unknown> | undefined,
): { ok: boolean; value: unknown } {
  if (typeof spec !== 'string') return { ok: spec !== undefined, value: spec }
  const formLower: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(formValues || {})) formLower[k.toLowerCase()] = v
  const headerLower: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(headerValues || formValues || {})) headerLower[k.toLowerCase()] = v
  const m = _ASSIST_WHOLE.exec(spec.trim())
  if (m) {
    const v = _assistantTokenValue(m[2], formLower, headerLower, m[1] as 'form' | 'header')
    return v === undefined || v === null ? { ok: false, value: undefined } : { ok: true, value: v }
  }
  if (/\{(form|header)\./.test(spec)) {
    let missing = false
    const text = spec.replace(_ASSIST_EMBED, (_s, scope: string, col: string) => {
      const v = _assistantTokenValue(col, formLower, headerLower, scope as 'form' | 'header')
      if (v === undefined || v === null) { missing = true; return '' }
      return String(v)
    })
    return missing ? { ok: false, value: undefined } : { ok: true, value: text }
  }
  return { ok: true, value: spec }
}

function _isParamObject(v: unknown): v is { value: unknown; protect?: boolean } {
  return !!v && typeof v === 'object' && !Array.isArray(v) && 'value' in (v as object)
}

// Build the host-facing request for a clicked 'open' action, or a string
// error naming what is misconfigured. Values are resolved here, once, against
// the row the button was clicked on — the target never sees the source row.
//   - a ``set`` value whose token is unresolved is dropped (the target keeps
//     its default rather than receiving a blank it did not ask for);
//   - an unresolved ``match`` value is reported in ``matchMissing``;
//   - an unresolved criterion is dropped, so the target's own prompt asks.
export function buildOpenActionRequest(
  actionName: string,
  binding: OpenActionBinding | undefined,
  sourceObjName: string,
  formValues: Record<string, unknown> | undefined,
  headerValues: Record<string, unknown> | undefined,
  onClosed?: () => void,
): OpenActionRequest | string {
  const b = binding || ({} as OpenActionBinding)
  const objName = String(b.obj_name || '').trim()
  if (!objName) return `Action "${actionName}" has no object to open configured.`
  const objType: OpenObjectType = OPEN_OBJ_TYPES.includes(
    String(b.obj_type || 'T').toUpperCase() as OpenObjectType,
  ) ? String(b.obj_type || 'T').toUpperCase() as OpenObjectType : 'T'
  const mode: OpenActionMode = OPEN_MODES.includes(b.mode as OpenActionMode)
    ? b.mode as OpenActionMode : 'add'

  const match: Record<string, unknown> = {}
  const matchMissing: string[] = []
  if (objType === 'T' && mode !== 'add') {
    for (const [col, spec] of Object.entries(b.match || {})) {
      if (!col.trim()) continue
      const r = resolveActionTemplate(spec, formValues, headerValues)
      if (r.ok) match[col] = r.value
      else matchMissing.push(col)
    }
    if (!Object.keys(match).length && !matchMissing.length) {
      return `Action "${actionName}" opens ${objName} in ${mode.replace(/_/g, ' ')} mode but names no columns to find the record by.`
    }
  }

  const set: OpenScreenPayload['set'] = []
  if (objType === 'T') {
    for (const entry of b.set || []) {
      const column = String(entry?.column || '').trim()
      if (!column) continue
      const r = resolveActionTemplate(entry.value, formValues, headerValues)
      if (!r.ok) continue
      set.push({
        column, value: r.value,
        itemChange: entry.item_change !== false,
        protect: !!entry.protect,
      })
    }
  }

  const params: Record<string, unknown> = {}
  const protectedParams: string[] = []
  if (objType !== 'T') {
    for (const [name, raw] of Object.entries(b.params || {})) {
      if (!name.trim()) continue
      const spec = _isParamObject(raw) ? raw.value : raw
      const r = resolveActionTemplate(spec, formValues, headerValues)
      if (!r.ok || r.value === '') continue
      params[name] = r.value
      if (_isParamObject(raw) && raw.protect) protectedParams.push(name)
    }
  }

  const then = objType === 'T'
    ? (b.then || []).filter((t) => t && (
      (t.run === 'line_command' && String(t.command || '').trim())
      || (t.run === 'action' && String(t.action_name || '').trim())))
    : []

  return {
    title: String(b.title || '').trim() || actionName,
    actionName,
    sourceObjName,
    objType,
    objName,
    mode,
    match,
    matchMissing,
    set,
    then,
    params,
    protectedParams,
    presentation: b.presentation === 'drawer' ? 'drawer' : 'navigate',
    onClosed,
  }
}

// The serialisable part of an open request (what the hand-off store keeps).
export function openScreenPayloadOf(req: OpenActionRequest): OpenScreenPayload {
  const { onClosed: _onClosed, ...payload } = req
  return payload
}

// ── Follow-up actions ───────────────────────────────────────────────────
// A post-save follow-up (notification / audit / side-effect). Historically a
// bare string `"event,action_name:logic"` where logic is a business_logic /
// Call-API call-string. Now EITHER that legacy string OR a structured
// ``FollowupActionDef`` carrying the same business-logic fields (function name,
// description, language, functional specification) as every other surface.
export interface FollowupActionDef {
  // When the follow-up fires: on-add / on-edit / on-delete / combinations.
  event: string
  // Display name of the follow-up action.
  name: string
  business_logic?: BusinessLogicValidation
  rest_api?: RestApiValidation
  // Calls (emits) an integration event defined in the project's
  // Integration design — the engine writes an IOFlow outbox row for it.
  ioflow_event?: { event_name: string }
  // Sends a message automatically (the same definition a send BUTTON uses,
  // with ``template`` instead of ``default_template`` and an optional
  // ``when`` rule). ``event`` may also be ``on-action:<button>`` — after
  // that button succeeds.
  send?: SendActionBinding & { template?: string; when?: SendWhenRule | null }
  // Runs a Transaction Wizard UNATTENDED on the server once the save / the
  // button committed (docs/plans/wizard-external-website-step.md §8). Every
  // step on its path must need no person (Check: NOT_UNATTENDED). A `fill`
  // wizard of this transaction writes back onto the record that triggered it.
  wizard?: { obj_name: string; param_map?: Record<string, unknown> }
  // Off without deleting it (the engine skips a disabled follow-up).
  disabled?: boolean
  // True when the entry is maintained by the Integration designer (mirrors
  // a design event's source binding); the reconciler moves/removes it when
  // the event definition changes.
  managed?: boolean
  // Legacy carrier kept in sync for back-compat (see ActionDef.logic).
  logic?: string
}

export type FollowupAction = string | FollowupActionDef

export function isFollowupActionDef(a: FollowupAction | null | undefined): a is FollowupActionDef {
  return !!a && typeof a === 'object' && typeof (a as FollowupActionDef).event === 'string'
}

// Upgrade a legacy `"event,action_name:logic"` string (or a partially-structured
// def whose logic is still a call-string) into a normalised FollowupActionDef.
export function normalizeFollowupAction(a: FollowupAction): FollowupActionDef {
  if (isFollowupActionDef(a)) {
    if (a.business_logic || a.rest_api || a.ioflow_event || a.send || a.wizard) return a
    const fromStr = legacyItemChangeToMode(a.logic || '')
    if (fromStr?.mode === 'business_logic') return { ...a, business_logic: fromStr.business_logic }
    if (fromStr?.mode === 'rest_api') return { ...a, rest_api: fromStr.rest_api }
    return a
  }
  const str = (a || '').toString()
  const commaIdx = str.indexOf(',')
  let event = 'on-add'
  let rest = str
  if (commaIdx !== -1) { event = str.substring(0, commaIdx); rest = str.substring(commaIdx + 1) }
  const colonIdx = rest.indexOf(':')
  const name = colonIdx === -1 ? rest : rest.substring(0, colonIdx)
  const logic = colonIdx === -1 ? '' : rest.substring(colonIdx + 1)
  const def: FollowupActionDef = { event, name }
  const fromStr = legacyItemChangeToMode(logic)
  if (fromStr?.mode === 'business_logic') def.business_logic = fromStr.business_logic
  else if (fromStr?.mode === 'rest_api') def.rest_api = fromStr.rest_api
  else if (logic) def.logic = logic
  return def
}

// The business-logic call-string for a follow-up (for the engine / deploy).
export function followupLogicString(a: FollowupAction): string {
  const def = normalizeFollowupAction(a)
  if (def.business_logic?.method) return businessLogicCallString(def.business_logic)
  if (def.rest_api?.url) return `rest_service('${def.rest_api.url}')`
  return def.logic || ''
}

/** ``lookup_xref`` — THEIR codes / descriptions → OUR code, per scope. */
export interface LookupXref {
  table: string
  /** The column holding their code (matched case-insensitively). */
  match_column: string
  /** More of their columns to match on (their description). */
  also_match?: string[]
  /** The column holding our code (the lookup's data value). */
  code_column: string
  /** ``{xref column: "header.<field>" | "form.<field>"}`` — e.g. the customer. */
  scope?: Record<string, string>
  /** Confirmed matches from an import are added as new rows. */
  learn?: boolean
}

export interface LookupResolve {
  min_score?: number
  margin?: number
  /** false = exact rungs only (code, name, cross-reference, alias). */
  semantic?: boolean
}

export interface ColumnDef {
  name: string
  col_id: string
  group_name: string
  // Canonical registry types + legacy aliases. `(string & {})` keeps the
  // union open for native/exotic tokens on adopted external tables while
  // preserving autocomplete for the canonical set.
  col_type: 'CHAR' | 'VARCHAR' | 'TEXT' | 'CLOB' | 'RICHTEXT'
    | 'NUMBER' | 'NUMERIC' | 'DECIMAL' | 'INTEGER' | 'BIGINT' | 'SMALLINT'
    | 'DOUBLE' | 'FLOAT' | 'REAL'
    | 'DATE' | 'DATETIME' | 'TIMESTAMP' | 'TIMESTAMPTZ' | 'TIME' | 'INTERVAL'
    | 'BOOLEAN' | 'JSON' | 'JSONB' | 'UUID' | 'BYTEA'
    | (string & {})
  heading: string
  // Optional: the caption in LIST / grid / filter UI, where it differs from
  // the form caption (a legacy browse screen's column title). Unset ⇒
  // ``heading``. Read through utils.listHeadingOf.
  list_heading?: string
  descr: string
  key: boolean | string
  default_value: string
  protect: boolean | string
  mandatory: boolean | string
  // Optional minimum number of characters a TYPED value must have (rich text
  // counts its visible text). Absent = no minimum. Enforced by the engine on
  // save; a blank value is left to `mandatory`. The form shows a live counter.
  min_length?: number | string
  db_name: string
  table_name: string
  db_size: string
  width: number
  x: string
  y: string
  // Optional: the legacy (Vision) edit screen's x position, as a string.
  // Breaks ties between fields on the same form row (same ``y``) so they keep
  // the left-to-right order of the original screen.
  screen_x?: string
  hidden: boolean | string
  // Optional: visibility in the LIST grid only, where it differs from the
  // form's. Unset/'' follows `hidden`; '1' leaves the column out of the list
  // though the form shows it; '0' lists it though the form hides it. Reverse
  // deploy sets it where the legacy browse and edit screens disagree.
  list_hidden?: boolean | string
  format: string
  // Optional: force typed text into one case as it is entered — ``upper`` for
  // codes (a legacy screen's <case>upper</case>). The engine applies the same
  // rule on every write, whoever sends the value.
  text_case?: 'upper' | 'lower'
  // Optional: a COMPUTED column — a SQL expression the database evaluates on
  // read (a description from a function, a balance, a label). Name a table of
  // this form as ``{TABLE}`` (main, joined, or a join's alias):
  // ``fn_descr({CUSTOMER}.reas_code)``. Always read-only; never saved, never
  // created as a table column.
  sql_expression?: string
  // Optional FORM layout (the list grid still shows the heading):
  //  - hide_label: no caption on the form — a second address line, a
  //    description shown beside its code (a legacy screen draws none).
  //  - inline_with: the NAME of the field this one sits beside, on the same
  //    line inside that field's cell (ITEMSER_DESCR beside ITEM_SER, TELE2
  //    beside TELE1). Ignored when that field is not shown in the same group.
  hide_label?: boolean | string
  inline_with?: string
  edit_mask: EditMask
  // Optional: back an image/document column with a document REPOSITORY
  // (obj_type "K") instead of the flat document store. The column still
  // holds a URL — every widget, grid, print view and export is unchanged —
  // but the file becomes a controlled document: rights, versions, retention
  // and an audit trail. Absent on nearly every column, and absence means the
  // ordinary file store.
  repository?: { obj: string; doctype?: string; folder?: string }
  lookup: string
  // Raw WHERE-clause fragment appended to the lookup query. Hand-edited
  // when the user picks "Advanced (SQL)" in the filter editor. When the
  // structured ``lookup_filter_rules`` is also present, the runtime
  // compiles the rules into SQL and ignores this field.
  lookup_filter?: string
  lookup_filter_rules?: LookupFilterRules
  // Advanced (raw SQL) lookup. When non-empty, the lookup is driven by this
  // full SELECT instead of the declarative ``lookup`` spec + filter: it must
  // expose two output columns named ``display`` and ``data`` and may
  // reference current-form / header values with {form.COL} / {header.COL}
  // placeholders (bound as parameters server-side). Takes precedence over
  // ``lookup`` / ``lookup_filter`` / ``lookup_filter_rules``.
  lookup_sql?: string
  // Custom title for the lookup picker window. Shared by both declarative
  // (table) and SQL lookups. When empty, the picker falls back to
  // ``Select <heading|name>``.
  lookup_title?: string
  // obj_name of the transaction whose form opens when the user clicks
  // "+ Add" in the lookup picker to create a new master record (which is
  // then auto-selected back into this field). Shared by both modes. When
  // empty, declarative (table) lookups still fall back to resolving the
  // owning transaction from the lookup table name.
  lookup_add_object?: string
  // Line Entry shortcut (line forms only): offer "Add <column>…" on the lines
  // toolbar — a multi-select over THIS column's lookup that adds one line per
  // ticked row, each through the form's own defaults / row_add / item_change.
  // Synthesises a `master` line source; no Line Entry block needed.
  lookup_add_lines?: boolean | string
  // SQL mode only: which output column of ``lookup_sql`` holds the value
  // that gets stored when the user clicks a row — a column name or a
  // 1-based position (e.g. ``"2"``). When empty, defaults to an output
  // column named ``data`` if present, otherwise the first column. Other
  // selected columns are shown alongside it in the picker grid.
  lookup_sql_data_col?: string
  // How a WRITTEN value (a file import, the Assistant) becomes this lookup's
  // code when it is not already one: a cross-reference table of the other
  // party's own codes → ours, optionally scoped by another value of the
  // record (the order's customer). See `value_resolve.py`. Absent ⇒ exact
  // name, then the master's aliases, then closest match by description.
  lookup_xref?: LookupXref
  // Thresholds for a description match on THIS column — override the
  // master's own (`transaction.vector_search.min_score` / `margin`).
  lookup_resolve?: LookupResolve
  // Vision POPHELP.MULTI_OPT, imported as-is: above 0 (1, 2, …) = the picker
  // ticks several rows and the field stores them comma-joined; 0 / absent =
  // one row.
  lookup_multi?: number
  // A Vision pophelp reused from another field (POPHELP fell back to a
  // generic mod's row): ``field_name`` is the field it was written for, whose
  // SQL_INPUT refs also mean this field.
  lookup_inherited_from?: { mod_name?: string; field_name?: string; sql_hash?: string }
  item_change: ItemChange | string | null
  validations: Validation[]
  // Optional list/table DISPLAY format (the designer "Display as" panel).
  // Display-only — never affects editing or the stored value. Rendered by the
  // shared <DisplayCell> on every list surface. See ./displayFormat.
  display?: DisplayFormat
  // Marks the column as SENSITIVE: the engine encrypts it before it reaches
  // the database and masks it on the way back out. Storage-level, not a
  // display option — the physical column holds an encryption envelope, so a
  // database dump, a read replica or a BI connection sees nothing readable.
  // See twasta_runtime_engine/field_crypto.py.
  secure?: SecureColumn
  // Numbering rules for a `series`-driven default: `auto_generate_device()`
  // (per-device, uses `prefix`) or `auto_generate_prefixed()` (per-prefix,
  // uses `parts`). Ignored for every other default_value.
  series?: SeriesConfig
  // Additional attributes (packages/page-runtime/src/uda). Never written by
  // the transaction designer: `uda` is the marker an INJECTED attribute column
  // carries (the engine injects it; the browser gets it from `uda_resolve` and
  // merges it in with uda/merge.ts), `in_grid` promotes an attribute to the
  // list / line grid, `pictures` holds a picture or swatch per value. Absent
  // on every ordinary column. (uda/model.py DESIGNER_COLUMN_KEYS lists these
  // as attribute-only keys, never tenant-delegable designer properties.)
  uda?: UdaColumnMarker
  in_grid?: boolean | string
  pictures?: UdaPictures
}

// ── Series-driven numbering (auto_generate_device / auto_generate_prefixed) ──

// Where one segment of a composed prefix comes from.
//   form   — a column on THIS form (optionally a substring of it)
//   const  — fixed text
//   date   — the server date at insert, so the counter restarts each period
//   lookup — a value read from a master table the form never shows (e.g. the
//            continent of the entered country)
export type SeriesPartSource = 'form' | 'const' | 'date' | 'lookup'

export type SeriesDateFormat = 'YY' | 'YYYY' | 'MM' | 'DD' | 'YYYYMM' | 'YYMM'

export interface SeriesPart {
  source: SeriesPartSource
  // form: the source column's NAME. Must be on the same form and mandatory —
  // a row saved without it cannot be numbered.
  column?: string
  // 1-based start (default 1) and optional length; omitting length takes the
  // rest of the value and makes ids vary in length. Apply to `form` and to the
  // value a `lookup` returns.
  start?: number
  length?: number
  transform?: 'upper' | 'lower' | 'none'
  // const
  text?: string
  // date
  format?: SeriesDateFormat
  // lookup: SELECT value_column FROM table WHERE match_column = <the form's
  // value of match_form_column>
  table?: string
  match_column?: string
  value_column?: string
  match_form_column?: string
}

export interface SeriesConfig {
  // auto_generate_device() only — the literal the device code follows.
  prefix?: string
  // auto_generate_prefixed() only — the ordered segments of the prefix.
  parts?: SeriesPart[]
  // Sits between the composed prefix and the number. Defaults to '-' for the
  // device variant and '' for the prefixed one.
  separator?: string
  // Zero-padded width of the counter (default 4). It never truncates: a series
  // that genuinely overflows grows a digit rather than repeating a number.
  width?: number
}

// The `secure` block of a column. Absent = an ordinary, plaintext column.
export interface SecureColumn {
  // encrypt = reversible (an ID number, a salary, a bank account).
  // hash     = one-way, for a value that only ever needs verifying (a PIN).
  mode?: 'encrypt' | 'hash'
  // 'exact' builds a blind index alongside the ciphertext so the column can
  // still be searched by exact value, used in a lookup / must_exist_in and
  // made unique. 'none' means the column cannot be filtered at all.
  search?: 'none' | 'exact'
  // Roles allowed to see the real value. EMPTY MEANS NOBODY — a value can be
  // written and never read back, which is the right default for a secret.
  reveal_roles?: string[]
  // How the role list reads. 'allow' (default) = only `reveal_roles` see the
  // value. 'deny' = every signed-in user holding a role sees it EXCEPT
  // `hide_roles`; a caller with no roles (anonymous visitor) is still masked.
  reveal?: 'allow' | 'deny'
  hide_roles?: string[]
  // What everyone else sees. Same vocabulary as BI column security.
  mask?: 'partial' | 'replace' | 'null' | 'hash'
  // Literal used by the 'replace' mask (default "CONFIDENTIAL").
  replacement?: string
  // What happens on a replicated (offline) device, which holds no key:
  // 'deny' omits the column entirely, 'masked' ships the mask.
  offline?: 'deny' | 'masked'
  // Explicit off switch that survives a round-trip through the JSON editor.
  enabled?: boolean
}

// Upgrade a legacy item_change code string to a structured mode. A
// `business_logic('fn','desc','lang')` call → business_logic; a
// `rest_service('url','desc')` / `rest_api(...)` call → rest_api. Anything
// unrecognised stays {mode:'code'} so it isn't lost (the engine only executes
// the business_logic reference inside it).
function legacyItemChangeToMode(code: string): ItemChange | null {
  const raw = (code || '').trim()
  if (!raw) return null
  const m = raw.match(/^(\w+)\((.*)\)$/s)
  if (m) {
    const fn = m[1].toLowerCase()
    const args = parseCallArgs(m[2] || '')
    if (fn === 'business_logic') {
      return {
        mode: 'business_logic',
        business_logic: {
          method: args[0] || '', description: args[1] || '',
          language: (args[2] as BusinessLogicValidation['language']) || undefined,
        },
      }
    }
    if (fn === 'rest_service' || fn === 'rest_api') {
      return { mode: 'rest_api', rest_api: { url: args[0] || '', on_error: 'allow' } }
    }
  }
  return { mode: 'code', code }
}

// Legacy item_change values may be plain strings (old saved pages) or null/undefined/empty.
// Normalize to the structured ItemChange | null form for editor & runtime use.
// The active modes are rule / business_logic / rest_api; a legacy 'code' value
// (or bare string) is upgraded to business_logic / rest_api where recognisable.
export function normalizeItemChange(value: ItemChange | string | null | undefined): ItemChange | null {
  if (value == null) return null
  if (typeof value === 'string') {
    return legacyItemChangeToMode(value)
  }
  if (typeof value === 'object') {
    const mode = (value as ItemChange).mode
    if (mode === 'rule' || mode === 'business_logic' || mode === 'rest_api') {
      return value
    }
    if (mode === 'code') {
      // Tolerate the invalid {mode:'code', business_logic:{...}} /
      // {mode:'code', rest_api:{...}} hybrids some agent-authored models
      // carry — the structured payload wins over the (absent) code string.
      const hybrid = value as { business_logic?: BusinessLogicValidation; rest_api?: RestApiValidation }
      if ((hybrid.business_logic?.method || '').trim()) {
        return { mode: 'business_logic', business_logic: hybrid.business_logic! }
      }
      if ((hybrid.rest_api?.url || '').trim()) {
        return { mode: 'rest_api', rest_api: hybrid.rest_api! }
      }
      return legacyItemChangeToMode((value as ItemChangeCode).code || '')
    }
  }
  return null
}

export function itemChangeSummary(value: ItemChange | string | null | undefined): string {
  const ic = normalizeItemChange(value)
  if (!ic) return ''
  if (ic.mode === 'business_logic') {
    const m = (ic.business_logic?.method || '').trim()
    const lang = ic.business_logic?.language
    return `Business Logic${m ? `: ${m}` : ''}${lang ? ` (${lang})` : ''}`
  }
  if (ic.mode === 'rest_api') {
    const url = (ic.rest_api?.url || '').trim()
    return `Call API${url ? `: ${url}` : ''}`
  }
  if (ic.mode === 'code') {
    const lineCount = (ic.code.match(/\n/g)?.length ?? 0) + (ic.code.length > 0 ? 1 : 0)
    return `Code · ${lineCount} line${lineCount === 1 ? '' : 's'}`
  }
  const all = ic.fill || []
  const exprCount = all.filter(f => (f.expr || '').trim() && !(f.table_col || '').trim()).length
  const lookupCount = all.length - exprCount
  const parts: string[] = []
  if (lookupCount > 0) {
    const tbl = ic.source_table || '(no table)'
    parts.push(`${tbl} · ${lookupCount} fill${lookupCount === 1 ? '' : 's'}`)
  }
  if (exprCount > 0) {
    parts.push(`${exprCount} expr${exprCount === 1 ? '' : 's'}`)
  }
  const protectCount = (ic.protect || []).length
  if (protectCount > 0) {
    parts.push(`${protectCount} protect${protectCount === 1 ? '' : 's'}`)
  }
  return `Rule: ${parts.join(' · ') || '0 fills'}`
}

// One-line summary of a column's lookup config for the Property Panel row.
// Empty string when no lookup is configured.
export function lookupSummary(
  col: Pick<ColumnDef, 'lookup' | 'lookup_sql' | 'lookup_filter' | 'lookup_filter_rules'>,
): string {
  const sql = (col.lookup_sql || '').trim()
  if (sql) {
    const lines = sql.split('\n').length
    return `SQL query · ${lines} line${lines === 1 ? '' : 's'}`
  }
  const spec = (col.lookup || '').trim()
  if (!spec) return ''
  const m = spec.match(
    /^([A-Za-z_][A-Za-z0-9_]*)\s*\(\s*([A-Za-z_][A-Za-z0-9_]*)\s*,\s*([A-Za-z_][A-Za-z0-9_]*)\s*\)\s*$/,
  )
  let base = m ? `${m[1]} · ${m[2]} → ${m[3]}` : spec
  let nFilters = 0
  if (col.lookup_filter_rules) {
    nFilters = (col.lookup_filter_rules.conditions || [])
      .filter(c => (c.col || '').trim()).length
  } else if ((col.lookup_filter || '').trim()) {
    nFilters = 1
  }
  if (nFilters > 0) base += ` · ${nFilters} filter${nFilters === 1 ? '' : 's'}`
  return base
}

export interface JoinDef {
  table: string
  main_table: boolean
  table_descr?: string
  join_table?: string
  join_type?: 'inner' | 'left' | 'right' | 'full'
  column: string | string[]
  join_column: string | string[]
}

export interface JoinConfig {
  join_predicates: {
    joins: JoinDef[]
  }
}

/** Comparison a row-security rule makes (engine `row_security_rules.OPS`).
 *  Absent = `eq`. */
export type RowSecurityOp = 'eq' | 'in' | 'like' | 'between' | 'gt' | 'gte' | 'lt' | 'lte'

/** One row-security condition: `column <op> <what the source resolves to for
 *  the current user>`. */
export interface RowSecurityRule {
  column: string
  /** `user_id` | `tenant_id` | a project-variable token (`:branch`) or bare
   *  variable name. Anything the engine cannot resolve owns NOTHING. Ignored
   *  when `sql` is set. For `between` this is the LOW bound. */
  source?: string
  /** Default `eq`. Only `eq` stamps the column on add (an `in` with exactly
   *  one value fills a blank one); every other op CHECKS the written value. */
  op?: RowSecurityOp
  /** A read-only SELECT whose result is the value(s): `in` = every row's
   *  first column; `between` = the first row's two columns (low, high);
   *  otherwise the first row's first column. `:user_id` / `:user_email` /
   *  `:tenant_id` / `:project` and declared project variables are bound. */
  sql?: string
  /** `between` only: the HIGH bound's source (when not using `sql`). */
  source_to?: string
}

/** Authored either as a single rule (legacy, still written when there is
 *  exactly one) or as a `rules` list, ALL of which must match. */
export interface RowSecurity extends Partial<RowSecurityRule> {
  rules?: RowSecurityRule[]
  /** Roles the scope does NOT apply to — for a caller holding one the object
   *  behaves as if unsecured: no filter, no ownership 404, and no owner stamp
   *  (so an admin editing another scope's record does not move it). Empty /
   *  absent = applies to everybody, which is how every model read before. */
  exempt_roles?: string[]
}

export interface SqlModel {
  form_title: string
  form_descr: string
  form_no: string
  // Immediate parent form in the transaction hierarchy. Absent / empty /
  // equal to "1" / equal to this form's own form_no ⇒ the form is a direct
  // child of the header (form "1"), preserving the historical positional
  // 2-level convention. Setting it to another child form's form_no nests this
  // form one level deeper (e.g. PO header "1" → Items "2" → Delivery Schedule
  // "3" with parent_form_no "2"). The header form has no parent.
  parent_form_no?: string
  // A DIRECT child of the header only: render this form as an editable table
  // inside the header group of that name (a header column's group_name) on the
  // record editor, instead of as a tab. Layout only — the rows save in the
  // same atomic batch as a tabbed detail form.
  display_in_group?: string
  // Caption of that inline table's add-row button (default "Add row").
  add_label?: string
  // Header groups (group_name) that start EXPANDED on the record form; by
  // default only the first group (and any hosting an inline table) is open.
  open_groups?: string[]
  /** Per-group presentation, keyed by the group's name as its columns carry
   *  it ('' → "Others"): a display `label`, an explicit `order`, and `open`
   *  (starts expanded). Authorable, and customizable per organization. */
  group_settings?: Record<string, { label?: string; order?: number | string; open?: boolean | string }>
  /** Data columns kept in view (frozen) in this form's grid — the record list
   *  for the header form, the line grid for a child form. Absent / 0 = none. */
  frozen_columns?: number | string
  // A detail form shown for reference only: its rows are listed but cannot be
  // added, edited or deleted on this screen (the engine refuses such writes).
  read_only?: boolean
  // A detail form whose rows only BUSINESS LOGIC writes (e.g. a header
  // item_change that fills a history tab): the screen shows no Add / Edit /
  // Delete, but the rows still save with the record. Unlike read_only the
  // engine does not refuse them.
  lines_locked?: boolean
  // The columns of a `lines_locked` line the user may still fill (a remark,
  // a photo): on an editable record the line opens with Save, these columns
  // open and every other one stays protected. Still no Add / Delete.
  lines_locked_editable?: string[]
  // Candidate rows the user TICKS (see RowSelectConfig): a tick box per row,
  // no Add / Edit / Delete, and only ticked rows are saved.
  row_select?: RowSelectConfig
  main_table: string
  // Form-level events. Same shape as a column ``item_change`` (an ItemChange:
  // a declarative rule of lookup + expression fills, a {mode:'code'} business-
  // logic call, or a legacy bare code string) — they "set values based on the
  // definition" exactly like item_change, but fire for the whole row rather
  // than on a single column change: row_add when a new blank row is created,
  // row_edit when an existing row is loaded for edit. Legacy plain strings are
  // still read (normalised to {mode:'code'}). (legacy: itm_default / itm_defaultedit)
  row_add?: ItemChange | string
  row_edit?: ItemChange | string
  // Fires when the user FINISHES a row (it passed its row validation and is
  // about to be kept in the buffer) — typically to rebuild a child form under
  // it, e.g. a POS line's tax lines. Same shape as row_add / row_edit plus the
  // optional `rebuild` / `when_changed` below. The function receives the row,
  // `_old` (the row before this edit), `_parent`, `_children` (the row's current
  // child rows) and `_lines`, and returns per child form replace / clear /
  // unchanged (see ItemChangeFormChange), plus values for the row and its
  // ancestors. See docs/row_complete_event.md.
  row_complete?: RowCompleteEvent | string
  // Per-row writes to OTHER tables, running inside the save transaction. Fires
  // once for every saved row of THIS form (never another form's), and an error
  // return rolls the whole tree back — including rows and cross-update writes
  // already applied for earlier rows — with the failure scoped to the offending
  // {form_no, row index}. This is where a stock / balance / ledger decrement
  // belongs. (`transaction.cross_updates` is the legacy placement: it fires for
  // every form's rows and only reports, so the save still commits.)
  cross_updates?: (ItemChange | string)[]
  // Row-level security: restrict each end user to rows they own. Each rule's
  // `column` is a main-table column stamped from the identity on add;
  // list/load/edit/delete are scoped to owned rows. `source` is `user_id` /
  // `tenant_id` or a project-variable token (`:branch`, `:device_user`, …)
  // resolved for the logged-in user. Several rules AND together, so a record
  // can be scoped by company AND branch at once. The legacy single-rule shape
  // ({ column, source }) is still read. Enforced by the runtime engine.
  row_security?: RowSecurity
  columns: { column: ColumnDef }[]
  actions: Action[]
  validations: Validation[]
  joins: JoinConfig
  // List-view behaviour for this form's record grid. Transactions grow
  // unbounded, so the list paginates server-side (newest first) and the
  // designer can enforce a mandatory date-range filter the end user may
  // re-range but not bypass. Optional — absent means defaults (page 50,
  // newest-first by the first date column, no enforced filter).
  list_view?: ListViewConfig
  // How ONE record of this form reads when it appears INSIDE something else —
  // a step in a process trail, a record behind a diagram node. Optional:
  // absent means the engine derives a layout from the form's own declarations
  // (see `process_trail.feed_view`), so a form nobody visits still reads.
  feed_view?: FeedViewConfig
  // How lines ARRIVE on this LINE form: sources of candidate rows (another
  // transaction's form, a master / a column's lookup, a business-logic
  // function, a General Process) and the commands the user sees on the lines
  // toolbar. Built server-side (`fetch_lines` / `apply_lines`), checked again
  // against the live source at save. See docs/plans/line-entry-development-plan.md.
  line_entry?: LineEntry
  // The view picked on this form's header in the designer's Design tab, saved
  // so Live Preview opens on the same view: on the header form 'form' → the
  // Form input interface, 'grid' → the records list, 'feed' → the record cards;
  // on a detail form 'feed' → cards, anything else → its grid. Absent ⇒ the
  // defaults (header Form, details Grid in the designer; list in Live Preview).
  view_layout?: PreviewMode
}

// ── Line Entry ──────────────────────────────────────────────────────────────

export type LineSourceKind = 'transaction' | 'master' | 'business_logic' | 'process'
export type LineCommandDocuments = 'header' | 'choose' | 'header_or_choose' | 'none'
export type LineCommandPicker = 'none' | 'review' | 'multi'

/** Bounds on ONE line column. Values are expressions in the validation
 *  language over `{src.X}` / `{header.X}` (min / max / one_of entries). */
export interface LineLimit {
  min?: string
  max?: string
  /** Absolute width beyond the bound: column unit, days (DATE), minutes (DATETIME). */
  tolerance?: number
  /** Percent of the bound (numbers). */
  tolerance_pct?: number
  one_of?: string[]
  /** Outside the bound but inside the tolerance: warn (acknowledgeable) or block.
   *  Beyond the tolerance is always refused; with no tolerance, `over` governs. */
  over?: 'warn' | 'block'
  /** This column drives the line's status chip (one per source). */
  status?: boolean
  status_labels?: [string, string, string]
}

/** Pre-filled values by line column: an expression, or "max" / "min" = that
 *  column's limit. */
export type LineFill = Record<string, string>

export interface LineSourceFrom {
  kind: LineSourceKind
  // transaction
  object?: string
  form_no?: string
  // master
  lookup_of?: string
  table?: string
  filter?: string
  key?: string
  // business_logic
  business_logic?: { method: string; language?: string }
  // process
  process?: string
  /** Criterion → header expression (a default the user may change on the
   *  criteria step), or `{ value, protect: true }`: read-only, and never taken
   *  from the browser (a receipt protects the PO number it pulls from). */
  params?: Record<string, LineProcessParam>
  /** "skip": land straight on the lines (default "ask" = criteria step first
   *  whenever an unprotected criterion exists). */
  criteria?: 'ask' | 'skip'
}

export type LineProcessParam = string | { value: string; protect?: boolean }

export interface LineSource {
  name: string
  label?: string
  from: LineSourceFrom
  documents?: {
    header_column?: string
    source_key?: string
    choose?: { lookup: string; filter?: string; multi?: boolean }
  }
  where?: string
  map?: Array<{ to: string; from: string; pin?: boolean }>
  fill?: LineFill
  link?: { to: string[]; from?: string[]; once?: boolean }
  limits?: Record<string, LineLimit>
  lock?: string[]
  picker?: {
    mode?: LineCommandPicker
    show?: string[]
    edit?: string[]
    item_change?: boolean
    validate?: 'limits' | 'object'
    on_invalid?: 'block_add' | 'skip_invalid'
    /** Line columns summed in the picker's footer (default: the status one). */
    totals?: string[]
  }
  on_document_change?: 'ask' | 'replace' | 'keep'
  max_rows?: number
  /** Set on a source the client synthesised from `lookup_add_lines`. */
  synthetic?: boolean
}

export interface LineCommand {
  name: string
  label?: string
  /** The line under the label in the menu ("Tick lines and change
   *  quantities"). Absent ⇒ described from its picker / fill. */
  description?: string
  source: string
  documents?: LineCommandDocuments
  picker?: LineCommandPicker
  fill?: LineFill
  validate?: 'limits' | 'object'
  shortcut?: string
  visible_when?: string
  synthetic?: boolean
}

export interface LineEntry {
  view?: 'grid' | 'popup'
  sources?: LineSource[]
  commands?: LineCommand[]
  toolbar?: { button?: string; primary?: string; menu?: string[] }
  on_header_change?: { column: string; do?: 'run' | 'offer' | 'none'; commands?: string[] }
  quick_add?: {
    source: string
    match?: string[]
    existing?: 'increment' | 'new_line'
    /** The line column a scan adds to (default: the source's status column). */
    quantity?: string
  }
}

// ── Feed view ───────────────────────────────────────────────────────────────
//
// Authored with the FEEDS VISUAL's own drop wells, and stored in its own shape
// (`visualLayout` ColumnGroups), so a designer who has laid out a Feeds visual
// already knows this screen and one renderer serves both. The three fields
// beside `visualLayout` are what a transaction has and a BI row does not: a
// sentence, a child form's lines, and an icon for the rail.

/** One column dropped into a well — a form column by db_name, or a literal
 *  caption (the object's own title is the usual one). */
export interface FeedColumnRef {
  name?: string
  constant?: string
  /** A heading printed before the value ("Due", "Raised by"). A card is read
   *  at a glance, so most values stand alone — this is for the ones that are
   *  ambiguous without a word in front of them. */
  label?: string
}

/** The Feeds visual's ColumnGroup shape, verbatim. */
export interface FeedColumnGroup {
  GroupName: string
  StandardName: string
  COLUMNS: FeedColumnRef[]
}

export interface FeedViewConfig {
  visualLayout?: FeedColumnGroup[]
  /** Rail glyph. Free-form; unrecognised names fall back to a document. */
  icon?: string
  /** A sentence about the record, with `{form.COLUMN}` tokens. Left empty by
   *  default: an invented sentence reads as fact. */
  narrative?: string
  /** A child form's lines, shown when the card is opened. */
  detail?: {
    form_no: string
    /** db_names, in the order they appear. */
    columns: string[]
    limit?: number
  } | null
}

// Operators for an enforced list condition. `between` is the RANGE form
// (from/to — available on every data type); the rest take a single value
// (`in` takes a comma-separated list).
export type EnforcedFilterOp =
  | 'between' | 'eq' | 'neq' | 'in'
  | 'gt' | 'gte' | 'lt' | 'lte' | 'contains' | 'starts_with'

// Which operators a filter condition may use on a column, by the column's
// own data type. The runtime input is built from the SAME type (see
// `filterInputKind`), so an operator is offered only where its input can be
// typed AND the comparison can run.
//
// `between` is offered on EVERY type: SQL ranges text lexically the same way
// it ranges numbers and dates ("deal_no from D001 to D100"), and the input is
// just two of whatever that column's single input is. What the type decides is
// the ORDER — a range leads for the kinds people range, and follows `=` for
// the kinds people match.
export function filterOpsForColumn(c?: ColumnDef): EnforcedFilterOp[] {
  switch (filterInputKind(c)) {
    case 'date':
    case 'datetime':
    case 'time':
      return ['between', 'eq', 'neq', 'gt', 'gte', 'lt', 'lte']
    case 'number':
      return ['between', 'eq', 'neq', 'in', 'gt', 'gte', 'lt', 'lte']
    case 'boolean':
      return ['eq', 'neq', 'between']
    case 'select':
      return ['eq', 'neq', 'in', 'gt', 'gte', 'lt', 'lte', 'between']
    default:
      return ['eq', 'neq', 'in', 'contains', 'starts_with',
        'gt', 'gte', 'lt', 'lte', 'between']
  }
}

// The INPUT a filter value is captured with — the designer's default-value
// editor and the runtime's filter bar both build from this one answer, so
// the two can never disagree about what a condition accepts. `select` means
// the column carries a fixed picklist (edit_mask ddlb / radio values).
export type FilterInputKind =
  'date' | 'datetime' | 'time' | 'number' | 'boolean' | 'select' | 'text'

export function filterInputKind(c?: ColumnDef): FilterInputKind {
  if (!c) return 'text'
  const t = (c.col_type || '').toUpperCase()
  if (t === 'DATE') return 'date'
  if (['DATETIME', 'TIMESTAMP', 'TIMESTAMPTZ'].includes(t)) return 'datetime'
  if (t === 'TIME') return 'time'
  if (['BOOLEAN', 'BOOL'].includes(t)) return 'boolean'
  // A picklist beats the underlying storage type: a status stored as a number
  // still filters by picking its label.
  const vals = c.edit_mask?.values
  if ((c.edit_mask?.mask === 'ddlb' || c.edit_mask?.mask === 'radio')
      && Array.isArray(vals) && vals.length > 0) return 'select'
  if (['NUMBER', 'NUMERIC', 'DECIMAL', 'INTEGER', 'INT', 'BIGINT', 'SMALLINT',
       'DOUBLE', 'FLOAT', 'REAL'].includes(t)) return 'number'
  return 'text'
}

// True for the two kinds a `:calendar_*` default resolves to a real value
// for. Offering a calendar token anywhere else writes a date into a
// comparison against a text/number column, which the database rejects.
export function acceptsCalendarDefault(c?: ColumnDef): boolean {
  const k = filterInputKind(c)
  return k === 'date' || k === 'datetime'
}

export interface EnforcedCondition {
  // db_name of a main-table column.
  column: string
  // Caption for this condition on the filter bar. Absent ⇒ the column's own
  // heading, which is what a designer-authored condition normally wants. It
  // earns its place when one column carries two conditions ("Ordered after" /
  // "Ordered before"), and on a reverse-imported filter, which keeps the
  // caption the screen it came from prompted under.
  label?: string
  // For `between`: a caption for EACH bound, shown on its own input in place
  // of the generic "From" / "To". A range is prompted as two questions and is
  // often asked as two ("Manufactured from" / "Manufactured to"), so each end
  // can say what it is. Either may stand alone; the missing one keeps "From"
  // or "To". They caption the inputs, not the condition — `label` (or the
  // column heading) still captions the pair.
  label_from?: string
  label_to?: string
  // Comparison operator. `between` is the from–to range form, valid on any
  // column type (text ranges lexically).
  op: EnforcedFilterOp
  // For `between`: the default lower/upper bounds.
  default_from?: string
  default_to?: string
  // For every other op: the default value (`in` = comma-separated list).
  // Any default (incl. from/to) may be a literal, a calendar token
  // (:calendar_month_start, :calendar_today, … — date/datetime columns only)
  // or a project variable (:my_var) — resolved server-side at list time.
  default_value?: string
  // When true the end user sees the resolved value read-only (cannot change
  // it). The condition itself is never removable regardless.
  lock_values?: boolean

  // ── Filter-input configuration ──────────────────────────────────────────
  // A reference picker for the input, same shape as a column's own lookup
  // (`TABLE(DISPLAY, DATA)` + an optional narrowing filter, or raw SQL).
  // Absent ⇒ the engine falls back to the COLUMN's lookup, so a field that
  // is picked from a master in the form is picked the same way in the filter.
  // `lookup: ''` (explicitly blank) turns the inherited picker off.
  lookup?: string
  lookup_sql?: string
  lookup_filter?: string
  lookup_filter_rules?: LookupFilterRules
  lookup_title?: string
  lookup_sql_data_col?: string
  // Structured rules checked against the RESOLVED filter value before the
  // list query runs — the same rule shape (and the same engine) as a
  // column's validations. Severity 'E' refuses the list with the rule's
  // message; 'W'/'P' are shown as a notice on the filter bar and the list
  // still runs. `{form.X}` in an expression / message reads the OTHER
  // conditions of the same set.
  validations?: ValidationRule[]
}

// Legacy single date filter — superseded by `enforced_filters`. Still read by
// the engine for models saved before the multi-condition editor.
export interface EnforcedDateFilter {
  column: string
  default_from?: string
  default_to?: string
  lock_values?: boolean
}

// A named bundle of enforced conditions. The designer can define several
// (e.g. "Date + Status", "Customer + Order"); at runtime the end user picks
// ONE set (or bypasses to browse all records paginated). Deploy creates one
// composite index per set over its columns in the order they are listed.
export interface EnforcedFilterSet {
  // Stable slug, used for the index name and the active-set selection round
  // trip. Unique within the form.
  id: string
  // Human label shown in the runtime filter-set selector.
  name: string
  conditions: EnforcedCondition[]
  // The set applied on first load. Falls back to the first set when none is
  // marked default.
  is_default?: boolean
  // Whether the value inputs are OPEN when the list first draws. Unset ⇒
  // derived: a set whose every unlocked condition already has a default
  // opens collapsed (the data is on screen; the inputs are there to change
  // it), and a set that still needs a value from the user opens expanded.
  expand_inputs?: boolean
}

export interface ListViewConfig {
  // Rows fetched per page (server-side). Defaults to 50.
  page_size?: number
  // Ordered set of columns (by `name` or `db_name`) surfaced on the compact
  // (mobile-style) card list — matches the Android runtime's
  // `ListView.columns`. Optional: absent / empty ⇒ the compact view falls back
  // to the first few visible columns in grid order. Display-only; never affects
  // the desktop grid, editing, or the stored value.
  columns?: string[]
  // Named filter sets — the end user selects one at runtime. Supersedes the
  // single `enforced_filters` list (kept below for back-compat).
  enforced_filter_sets?: EnforcedFilterSet[]
  // @deprecated single (unnamed) condition list — read by the engine and the
  // designer as one implicit set when `enforced_filter_sets` is absent.
  enforced_filters?: EnforcedCondition[]
  // @deprecated legacy single date filter; use enforced_filter_sets.
  enforced_filter?: EnforcedDateFilter
  // Per-record edit lock. When the expression evaluates truthy for a record
  // (item-change expression syntax — bare names / {form.X}, e.g.
  // "{form.status} == 'APPROVED'"), that record becomes view-only on every
  // surface: the list shows an eye icon, the form opens read-only with a View
  // indicator, and the engine refuses edit/save on it. `reason` is the message
  // shown to the user. Absent / blank expression ⇒ no lock.
  edit_lock?: EditLockRule
  // An imported Vision screen's record states, from TRANSETUP's CONFIRM_COL /
  // CONFIRM_VAL, CANCEL_COL / CANCEL_VAL and CLOSE_COL / CLOSE_VAL: a record
  // whose column holds the value is confirmed / cancelled / closed. Read by
  // the buttons' OBJ_ACTIONS.SHOW_CONFIRM (see showConfirmAllows).
  vision_states?: VisionStates
  // An imported Vision screen's workflow (TRANSETUP.WORKFLOW_OPT 2 = initiated
  // on save, 4 = on submit; process_id = WRKFLW_INIT). While the engine
  // reports a record's workflow in progress (WF_PRC_STATUS —
  // ``_wf_in_progress`` / ``wf_in_progress``) it opens view-only and hides
  // Edit and Submit.
  vision_workflow?: VisionWorkflow
  // Built-in Add switch. `false` ⇒ the form has no Add button on any surface
  // (for every viewer, owner included) and the engine refuses an insert
  // through it — for a review screen whose records are created on another
  // object. Absent / true ⇒ Add follows the viewer's rights as usual.
  allow_add?: boolean
  // TABS above the list. Each tab is a condition the SERVER adds to the list
  // query (the browser only names the tab). Either the one-field shortcut —
  // `column` (a main-table field with a drop-down / radio picklist) and an
  // optional `values` subset / order — or explicit `items`, which win. See
  // txn/list/listTabs.ts (twin of the engine's list_tabs.py).
  tabs?: ListTabsConfig
  // The record editor's layout this screen opens in for someone who has not
  // chosen one (standard | simple | document | compact). Absent ⇒ standard
  // (compact on a phone). A person's own choice is remembered and wins.
  editor_layout?: 'standard' | 'simple' | 'document' | 'compact'
}

export interface ListTabCondition {
  column: string
  op: string
  value?: string | string[]
  from?: string
  to?: string
}

export interface ListTabItem {
  id: string
  label: string
  color?: string
  when: ListTabCondition[]
}

export interface ListTabsConfig {
  enabled?: boolean      // explicit off-switch keeping the config
  show_all?: boolean     // the "All" tab (default true)
  counts?: boolean       // records per tab (default true)
  default?: string       // tab opened first: 'all' or a tab id
  column?: string        // the one-field shortcut
  values?: string[]      // its values: subset + order (absent ⇒ every choice)
  items?: ListTabItem[]  // custom tabs (win over the shortcut)
}

export interface EditLockRule {
  // Truthy ⇒ the record is locked (read-only). Blank disables the rule.
  expression?: string
  // Human message shown beside the View indicator and returned by the engine
  // when a locked record's edit is refused.
  reason?: string
  // Explicit off-switch (keeps the authored expression without enforcing it).
  enabled?: boolean
}

// ── Approvals (multi-level, rule-based) ──────────────────────────────────
//
// A transaction can require a multi-level approval workflow before a record is
// committed. The designer defines an ordered list of approval LEVELS; each
// level optionally applies only when a condition over the record's columns
// holds, and is cleared by a user holding the level's project role (or one of a
// named set of users). The shape is persisted on the transaction model and
// PUBLISHED into an immutable workflow version — in-flight approvals keep
// running on their pinned version while later edits create a new one.
// One approver within a PARALLEL level (Phase 3 #16b): several sign-offs on the
// same step, gated by the level's quorum.
export interface ApprovalApprover {
  // The project role whose members may clear this slot (e.g. "legal").
  approver_role?: string
  // Or explicit user ids.
  approver_users?: string[]
  // Or a dynamic role expression evaluated per record (picks the role code at
  // runtime) — e.g. iif(amount > 100000, "senior_finance", "finance").
  role_expr?: string
  // Display label for the approver.
  name?: string
}

export interface ApprovalLevel {
  // Human label for the level (e.g. "Manager", "Finance").
  name: string
  // Applies-when expression over the record's columns (item-change expression
  // syntax — bare names / {form.X}); blank = the level always applies.
  // e.g. "amount > 100000". May reference a per-tenant override as ov_<name>.
  condition?: string
  // The project role whose members can clear this level (e.g. "finance").
  approver_role?: string
  // Optional explicit user ids who may clear this level — an alternative (or
  // addition) to a role.
  approver_users?: string[]
  // A DYNAMIC role expression evaluated per record (Phase 3 #16a) — picks the
  // approver role code at runtime. Used when there's no static approver_role.
  role_expr?: string
  // PARALLEL approvers on this one step (Phase 3 #16b). When set (length > 1),
  // the step needs a `quorum` of them rather than a single approver.
  approvers?: ApprovalApprover[]
  // How many of `approvers` must approve: 'all' | 'any' | a positive integer.
  quorum?: string
  // Hours allowed on this step before it is flagged overdue (Phase 3 #14b).
  sla_hours?: number
}

// A declared per-tenant override knob (Phase 3 #15a): a named value a tenant
// admin sets, referenced in a level condition as ov_<name>.
export interface ApprovalOverrideDecl {
  name: string
  default?: string
  label?: string
}

// One custom ACTION BUTTON's own approval chain. A transaction's saves and each
// of its buttons are INDEPENDENT workflows: "Cancel" can need the branch head
// while "Approve Credit" needs finance and an edit needs the line manager. The
// button's effect (its business logic / Call API) does not run when clicked —
// the request is staged, and the final approval replays it.
//
// Only server-side handlers can be deferred: 'system' (a client-side CRUD op),
// 'report', 'assistant' and 'import' actions have nothing to stage and are
// never routed ('import' writes through the ordinary batch save instead).
export interface ApprovalActionBinding {
  // The action button's name, exactly as authored on the form / transaction.
  action: string
  // Explicit off-switch; absent counts as on.
  enabled?: boolean
  // Optional display label for the approval inbox (defaults to `action`).
  label?: string
  // Stable workflow name; defaults to
  // `approval_${obj_name}_${action}` (slugified) so each chain versions apart.
  workflow?: string
  profile?: 'linear' | 'advanced'
  // Applies-when gate over the record: blank = every click needs approval,
  // otherwise only clicks where it holds (e.g. "amount > 100000"). May
  // reference a per-tenant override as ov_<name>.
  condition?: string
  // When true only one request for THIS button may be pending per record.
  single_active_per_record?: boolean
  // This chain's own ordered levels (cleared top to bottom).
  levels?: ApprovalLevel[]
  // Chain-specific override declarations; falls back to the object's.
  overrides?: ApprovalOverrideDecl[]
  // Emit approval.* lifecycle events to IOFlow for this chain.
  ioflow_events?: boolean
}

export interface ApprovalConfig {
  // Master switch — when false the transaction saves directly with no approval,
  // and no action button is routed either.
  enabled: boolean
  // Stable workflow name; defaults to `approval_${obj_name.toLowerCase()}`.
  workflow?: string
  // 'linear' is the rule-based level list authored by this editor; 'advanced'
  // is a graphical (XPDL) workflow published outside it.
  profile?: 'linear' | 'advanced'
  // Which save operations route through approval — a subset of ['add','edit'].
  // An EMPTY list means no save is routed (an object that only approves action
  // buttons); an ABSENT list keeps the historical add+edit default.
  triggers?: string[]
  // Per-action-button approval chains — independent of the save chain above.
  actions?: ApprovalActionBinding[]
  // When true only one approval may be in flight for a given record at a time.
  single_active_per_record?: boolean
  // The ordered approval levels (cleared top to bottom).
  levels?: ApprovalLevel[]
  // Declared per-tenant override knobs (Phase 3 #15a). The VALUES live per
  // tenant in twasta_approval_overrides; these are the names + seed defaults.
  overrides?: ApprovalOverrideDecl[]
  // Emit approval.* lifecycle events to IOFlow (Phase 3 #14).
  ioflow_events?: boolean
}

export interface KeyGeneration {
  mode?: string
  key_col?: string
  ref_ser?: string
  key_string?: string
}

/** `transaction.flow_form` — see TransactionModel.flow_form. */
export interface FlowFormBlock {
  flow_id: string
  flow_name?: string
  fields_hash?: string
  synced_at?: string
  notes?: string[]
}

export interface TransactionModel {
  obj_name: string
  // Content profile: this transaction publishes a web page per record
  // (content collections). See widgets/ContentPanel + backend content_collections.
  content?: import('./contentPanel').ContentProfileLite
  sql_models: { sql_model: SqlModel }[]
  // How the record key is generated at INSERT. ``mode: 'vision'`` (an imported
  // Vision screen) has the engine build ``key_col`` from the ref series, so
  // the add form shows that column as auto-generated and read-only.
  key_generation?: KeyGeneration
  actions: Action[]
  // Cross-table updates run inside the save transaction. Each entry is a
  // business-logic / Call-API call — modelled as an ItemChange (restricted to
  // business_logic / rest_api), or a legacy call-string still read by the engine.
  cross_updates: (ItemChange | string)[]
  // Post-save follow-up actions — a structured FollowupActionDef or a legacy
  // `"event,name:logic"` string.
  follow_up_actions: (FollowupAction | string)[]
  attach_docs: string[]
  // Transaction-level business-logic hooks (each an ItemChange restricted to
  // business_logic / rest_api, or a legacy call-string). pre_commit runs inside
  // the save transaction just before COMMIT — it can write to other tables
  // atomically and/or abort the save with an error. post_commit ("Transaction
  // Complete") runs after COMMIT for cleanup and never blocks the save.
  pre_commit?: (ItemChange | string)[]
  post_commit?: (ItemChange | string)[]
  // Optional multi-level, rule-based approval workflow. Absent / disabled means
  // the record saves directly. See ApprovalConfig.
  approval?: ApprovalConfig
  // This "transaction" is an IOFlow flow's INPUT FORM (nav obj_type "J",
  // docs/flow_forms.md): generated from the flow's inputs, never saved — its
  // one button runs the flow. Columns / forms carry `flow_var` (the variable
  // path the run reads); the server owns both.
  flow_form?: FlowFormBlock
  // Whether this object works on a disconnected device, and how its rows
  // travel back. Absent / not eligible = online only, which is the default and
  // leaves every existing object unaffected. When eligible, the offline lint
  // enforces the constraints that would otherwise fail silently on a device
  // (Python-only business logic, no rest_api, a stamped chg_date …).
  offline?: OfflineConfig
  // Semantic (vector) search over this object's records — set only on a
  // MASTER. Absent / disabled means nothing about the object changes.
  // See VectorSearchConfig.
  vector_search?: VectorSearchConfig
  // How FILES are imported into this screen (the import button, the
  // Assistant): who the sender is and what every reading must know. Per-
  // SENDER profiles (document kind, their instructions) are run-time data,
  // managed in the import window — see `import_profiles.py`.
  import_settings?: ImportSettings
  // The STANDARD additional attributes (packages/page-runtime/src/uda) — the
  // design layer: per form, the deciding columns, attribute sets, their
  // columns and what each organization may change (`tenant_may`). Edited in
  // the designer's Settings → Additional attributes; organizations add their
  // own on top at run time (never written into this file).
  uda?: UdaLayer
  // Whether the runtime Assistant may WRITE this object's content (a post, an
  // email, a job description) and under what draft/live lifecycle. A content
  // collection gets it from `content` without this. See AuthoringConfig.
  authoring?: AuthoringConfig
}

/**
 * What the runtime Assistant may WRITE on an object — read by
 * `twasta_assistant_engine/content_compose.py`.
 *
 * `fields` are the columns its writer fills (empty = the object's prose
 * columns: rich text, long text and free-text VARCHARs that are not keys,
 * look-ups or defaulted). `status_column` / `draft_value` / `live_value` are
 * the lifecycle: a new record the Assistant proposes is saved with the DRAFT
 * value unless the person presses Publish on its review card. On a content
 * collection all of it is inherited from `content`; `enabled: false` turns the
 * Assistant's writing off there.
 */
export interface AuthoringConfig {
  enabled?: boolean
  label?: string
  fields?: string[]
  status_column?: string
  draft_value?: string
  live_value?: string
  /** House style for the writer: voice, audience, structure, what to avoid. */
  guidance?: string
  /** Pictures the writer may put in the content (all allowed by default; used
   *  only when the user asks for them or the content has data to draw).
   *  Infographics are drawn from the brief's data — no image model; images and
   *  the cover call the owner's image model. */
  visuals?: {
    infographics?: boolean
    images?: boolean
    cover?: boolean
    /** Most pictures per record (default 4, at most 6). */
    max?: number
    /** Infographic accent colour, #rrggbb. */
    accent?: string
  }
  /** Image column a generated COVER goes into (a content collection's `image` role otherwise). */
  image_field?: string
  image_alt_field?: string
}

/**
 * How a MASTER's records are made findable by MEANING rather than by exact
 * spelling — so "Bob in the Mumbai sales office" resolves to one employee
 * code.
 *
 * `columns` is the load-bearing field, and the reason it is a LIST. Indexing a
 * master by its name alone reproduces the problem the feature exists to fix:
 * three employees called Sharma and no way to tell which one was meant. So the
 * identity column is embedded together with the attributes that separate
 * same-named records — department, branch, city — and the search matches
 * against all of them at once.
 */
export interface ImportSettings {
  /** The header lookup that says who sent a file (the order's customer).
   *  Absent ⇒ inferred: a lookup named like a party, else the first lookup. */
  sender_field?: string
  /** Instructions every reading of this screen's files follows, per file
   *  type; ``all`` applies to every type. A sender's own instructions are
   *  added after them and win where they disagree. */
  instructions?: { all?: string; pdf?: string; image?: string; docx?: string; spreadsheet?: string }
  /** false = never draft sender profiles after an import (default true). */
  learn_profiles?: boolean
  /** Where the sender MASTER keeps the details a document is matched by —
   *  `{tax_id: "gst_no", email: "email", phone: "mobile", city: "city",
   *  postal: "pin"}`: a first document from a known customer is then
   *  recognised with no AI (two kinds must agree). */
  sender_attributes?: Partial<Record<'tax_id' | 'email' | 'phone' | 'city' | 'postal', string>>
  /** The RECEIVING organization's own name / tax id / email — printed on every
   *  document it receives, so never a sender's keyword. */
  own_identifiers?: string[]
  /** Document types this screen receives, with the keywords that recognise
   *  them (seeded into the keyword registry; edited further in the import
   *  window's Keywords tab). */
  document_types?: Array<{ code?: string; label: string;
    patterns: Array<{ kind: string; value: string; zone?: string; weight?: number;
                      required?: boolean; exclude?: boolean }> }>
  /** The screen's default for "Recognise the sender and document type"
   *  (default true); each person's own choice, saved per screen, wins. */
  detect_documents?: boolean
  /** Master objects a sender's NAME is looked up in (vector index); absent =
   *  every indexed master. A sender in none of them is known by name. */
  sender_masters?: string[]
  /** Detection thresholds (import_rules.Thresholds) — blank uses the default. */
  detect?: { min_score?: number; min_kinds?: number; type_min_score?: number; margin?: number;
             title_lines?: number; heading_words?: number; phone_digits?: number }
}

export interface VectorSearchConfig {
  enabled: boolean
  /**
   * Form keys embedded for each record: the identity column FIRST, then the
   * disambiguators. Only own-table columns can be indexed (a joined display
   * column lives in another table).
   */
  columns: string[]
  /** The column a search RETURNS — the code other transactions store. */
  key_column?: string
  /** The column shown to a human in results. Defaults to columns[0]. */
  display_column?: string
  /**
   * Whether users may teach a record extra names ("we call Robert 'Bob'").
   * Aliases are folded into the record's vector, so they make it findable
   * without changing its data.
   */
  allow_alias?: boolean
  /**
   * Optional sentence template, e.g. "{EMP_NAME} works in {DEPARTMENT} at
   * {BRANCH}". Without one the columns are joined as "Label: value" pairs.
   */
  template?: string
  /**
   * How sure a DESCRIPTION match must be before an import / the Assistant
   * replaces a written value with this master's code (0–1, cosine). Below it,
   * or within `margin` of the runner-up, the person picks from candidates.
   * Absent ⇒ the engine default (0.55 / 0.05). A lookup column may override
   * both with `lookup_resolve`.
   */
  min_score?: number
  margin?: number
  /** True when the block was derived automatically from the model. */
  auto?: boolean
  /** Why those columns were chosen — shown in the designer. */
  reason?: string
}

export interface OfflineConfig {
  /** Opt in. Only an eligible object is linted for device constraints. */
  eligible: boolean
  /**
   * Which way rows move. `push` is the field-sales case: the device captures
   * and the server is the system of record. `pull` is reference data. Same
   * vocabulary as an external-DB sync mapping, deliberately.
   */
  sync?: 'push' | 'pull' | 'bidirectional'
  /**
   * Who wins when the same record changed on both sides while the device was
   * away. `device_wins` suits a captured sale (the device saw the customer);
   * `review` parks it for a human.
   */
  conflict?: 'device_wins' | 'server_wins' | 'last_writer_wins' | 'review'
  /** Higher drains first when the outbox flushes — invoices before surveys. */
  queue_priority?: number
}

export interface TransactionRoot {
  transaction: TransactionModel
}

// Tree node types for selection tracking
export type TreeNodeType =
  | 'object'
  | 'object-actions'
  | 'form'
  | 'columns'
  | 'column'
  | 'form-actions'
  | 'form-validations'
  | 'form-cross-updates'
  | 'form-joins'
  // Line Entry (sql_model.line_entry) — child forms only.
  | 'form-line-entry'
  | 'offline'
  | 'vector-search'
  | 'file-import'
  // Settings → Additional attributes (transaction.uda, the design layer).
  | 'additional-attributes'
  | 'assistant-authoring'
  // Transaction-level cross-updates: the LEGACY, non-blocking placement.
  // New ones belong on 'form-cross-updates'.
  | 'cross-updates'
  | 'pre-commit'
  | 'transaction-complete'
  | 'approvals'
  | 'followup-actions'
  | 'attachments'
  | 'work-rules'
  // Designer-only lists (the redesigned Transaction Designer's form / record
  // menus): a form's columns that react to a change, a form's row events, the
  // record's save steps, the record's settings index, and Publish on website.
  | 'form-field-events'
  | 'form-row-events'
  | 'record-saving'
  | 'record-settings'
  | 'website'

export interface TreeNode {
  type: TreeNodeType
  formIndex?: number
  columnIndex?: number
  actionIndex?: number
  label: string
  /** Designer only: which inspector tab to open (a column's General / Data /
   *  Display / Behaviour / Rules, a form's General / Data source / Access /
   *  List). Absent ⇒ the panel's own default. */
  tab?: string
}

// Layout options
export type LayoutMode = 'all' | 'center-only' | 'left-center' | 'center-right'

// Preview mode
export type PreviewMode = 'form' | 'grid' | 'feed'

// Device preview
export type DevicePreview = 'computer' | 'tablet' | 'mobile'

// ── Lookup filter (structured) ──────────────────────────────────────────
//
// A user-friendly representation of a lookup column's WHERE-clause
// filter. The designer renders this as a row-per-condition builder; the
// runtime calls compileLookupFilter() to produce safe SQL + a bound
// literal map to send to the backend.

export type LookupFilterOp =
  | 'eq' | 'ne' | 'gt' | 'lt' | 'gte' | 'lte'
  | 'contains' | 'starts_with' | 'ends_with'
  | 'is_null' | 'is_not_null'

export type LookupFilterSource = 'form' | 'header' | 'literal'

export interface LookupFilterCondition {
  // Column on the lookup table being constrained.
  col: string
  op: LookupFilterOp
  // ``source`` decides what kind of value the condition compares against.
  // For ``is_null`` / ``is_not_null`` the value is ignored.
  source: LookupFilterSource
  // For source='form' or 'header': the form column name to bind from.
  // For source='literal': the user-typed literal value (string).
  value: string
}

export interface LookupFilterRules {
  conditions: LookupFilterCondition[]
}

export const LOOKUP_OP_LABELS: Record<LookupFilterOp, string> = {
  eq: 'equals (=)',
  ne: 'not equals (≠)',
  gt: 'greater than (>)',
  lt: 'less than (<)',
  gte: 'greater or equal (≥)',
  lte: 'less or equal (≤)',
  contains: 'contains',
  starts_with: 'starts with',
  ends_with: 'ends with',
  is_null: 'is empty',
  is_not_null: 'is not empty',
}

// Ops that don't need a value picker.
export const LOOKUP_NULL_OPS = new Set<LookupFilterOp>(['is_null', 'is_not_null'])

// Plain SQL operator for the simple comparison ops.
const PLAIN_OP_SQL: Partial<Record<LookupFilterOp, string>> = {
  eq: '=', ne: '<>', gt: '>', lt: '<', gte: '>=', lte: '<=',
}

const IDENT_RE = /^[A-Za-z_][A-Za-z0-9_]*$/

export interface CompiledLookupFilter {
  sql: string
  literals: Record<string, unknown>
}

/**
 * Translate a structured filter into the placeholder-SQL shape the
 * livepreview backend expects. ``{form.X}`` / ``{header.X}`` references
 * bind to the form/header dicts on the LookupModal call; ``{lit.NAME}``
 * references bind to the returned ``literals`` map.
 *
 * Conditions that are incomplete or carry an invalid identifier are
 * silently skipped — this matches the "the user is mid-edit" reality of
 * an inline builder. Backend validation still rejects unsafe input.
 */
export function compileLookupFilter(
  rules: LookupFilterRules | null | undefined,
): CompiledLookupFilter {
  const literals: Record<string, unknown> = {}
  const parts: string[] = []
  const conditions = rules?.conditions || []
  conditions.forEach((c, i) => {
    const col = (c.col || '').trim()
    if (!col || !IDENT_RE.test(col)) return

    if (c.op === 'is_null') { parts.push(`${col} IS NULL`); return }
    if (c.op === 'is_not_null') { parts.push(`${col} IS NOT NULL`); return }

    // For form/header sources, the placeholder name MUST be a bare
    // identifier so the backend's substitution regex matches it.
    let valueExpr: string | null = null
    if (c.source === 'form' || c.source === 'header') {
      const ref = (c.value || '').trim()
      if (!ref || !IDENT_RE.test(ref)) return
      valueExpr = c.source === 'form' ? `{form.${ref}}` : `{header.${ref}}`
    } else if (c.source === 'literal') {
      const key = `l${i}`
      // contains / starts_with / ends_with wrap with % around the
      // literal so the LIKE pattern is correct without the user having
      // to type wildcards.
      let v: unknown = c.value
      if (c.op === 'contains') v = `%${c.value || ''}%`
      else if (c.op === 'starts_with') v = `${c.value || ''}%`
      else if (c.op === 'ends_with') v = `%${c.value || ''}`
      literals[key] = v
      valueExpr = `{lit.${key}}`
    } else {
      return
    }

    const plainOp = PLAIN_OP_SQL[c.op]
    if (plainOp) {
      parts.push(`${col} ${plainOp} ${valueExpr}`)
      return
    }
    if (c.op === 'contains' || c.op === 'starts_with' || c.op === 'ends_with') {
      // Form/header LIKE: concatenate wildcards with the bound value so
      // we never have to splice the value into SQL.
      if (c.source === 'form' || c.source === 'header') {
        const ref = (c.value || '').trim()
        const placeholder = c.source === 'form'
          ? `{form.${ref}}`
          : `{header.${ref}}`
        const pattern = c.op === 'contains'
          ? `'%' || ${placeholder} || '%'`
          : c.op === 'starts_with'
            ? `${placeholder} || '%'`
            : `'%' || ${placeholder}`
        parts.push(`${col} LIKE ${pattern}`)
        return
      }
      // Literal source — pattern was pre-wrapped above.
      parts.push(`${col} LIKE ${valueExpr}`)
      return
    }
  })
  return { sql: parts.join(' AND '), literals }
}

/**
 * True when the rules object has at least one usable (validated)
 * condition. Empty / all-blank rules compile to an empty SQL string
 * and should be treated as "no filter".
 */
export function lookupFilterIsActive(
  rules: LookupFilterRules | null | undefined,
): boolean {
  if (!rules) return false
  return compileLookupFilter(rules).sql.length > 0
}
